/**
 * Landing builder — preview renderer + block inspector helpers.
 * Verbatim port of the prototype's LandingPreview, RenderedBlock, BlockInspector, and form helpers.
 */
import { useEffect, useRef, useState } from "react";
import { Avatar, Btn, Checkbox, Dropdown, Icon, IconBtn, Input, Pill, Textarea, Toggle, T, alpha, tx } from "@v0y4ge/jrni-ui";
import { submitPublicEventRegistration } from "@v0y4ge/api-client";
import { getEventPublicPaymentConfig, formatMinor, type EventPublicPaymentConfig } from "../../lib/payments-api";
import { NumberField } from "../../lib/NumberField";
import { PayPalCheckout } from "./PayPalCheckout";
import { AdyenCheckout } from "./AdyenCheckout";
import { useT, t as i18nT, formatICU, currentLocale } from "../../lib/i18n";
import { wallClockToIso, tzNameLabel } from "../../lib/datetime";

// ── Color conversion helpers ────────────────────────────────────────
function clamp01(n: number) { return Math.max(0, Math.min(1, n)); }
function hexToRgb(hex: string): [number, number, number] {
  let h = (hex || "").replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return [255, 255, 255];
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function rgbToHex(r: number, g: number, b: number): string {
  const c = (n: number) => Math.round(clamp01(n / 255) * 255).toString(16).padStart(2, "0");
  return "#" + c(r) + c(g) + c(b);
}
function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60; if (h < 0) h += 360;
  }
  return [h, max ? d / max : 0, max];
}
function hsvToRgb(h: number, s: number, v: number): [number, number, number] {
  const c = v * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}
function hexToHsv(hex: string): [number, number, number] { const [r, g, b] = hexToRgb(hex); return rgbToHsv(r, g, b); }
function hsvToHex(h: number, s: number, v: number): string { const [r, g, b] = hsvToRgb(h, s, v); return rgbToHex(r, g, b); }

// ── Color favorites (persisted for demo/testing) ────────────────────────
const COLOR_FAV_KEY = "cd:studio:color-favorites:v1";
function readColorFavorites(): string[] {
  try { const raw = typeof localStorage !== "undefined" ? localStorage.getItem(COLOR_FAV_KEY) : null; const a = raw ? JSON.parse(raw) : []; return Array.isArray(a) ? a : []; } catch { return []; }
}
function writeColorFavorites(list: string[]) {
  try { if (typeof localStorage !== "undefined") localStorage.setItem(COLOR_FAV_KEY, JSON.stringify(list.slice(0, 24))); } catch { /* noop */ }
}

// ui-raw: shared style for compact 22px borderless icon buttons (row
// move-up/down/duplicate/remove actions). IconBtn's default is a 36px
// bordered+shadowed control — not a parity-safe swap for this deliberately
// minimal inline-row affordance, so every raw <button style={miniBtn}> below
// stays raw.
export const miniBtn: React.CSSProperties = {
  width: 22, height: 22, border: "none", background: "transparent",
  borderRadius: 3, color: T.n1, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
} as unknown as React.CSSProperties;

// ── Full-bleed layout (Req 1, Option-2 docket 2026-07-14) ───────────────
// The page FRAME is fluid/full-bleed; each block constrains its CONTENT to an
// internal max-width for readability. Container queries in LandingPreview
// reflow grids/hero as the page narrows — the same rules govern the builder
// canvas and the attend page, because they are the same renderer.
const lpInner: React.CSSProperties = { maxWidth: 1080, margin: "0 auto", width: "100%", boxSizing: "border-box" };

// ── Data types ─────────────────────────────────────────────────────────
export interface LandingBlock { uid: string; type: string; cfg: Record<string, unknown>; }
export interface LandingTheme { id: string; name: string; brand: string; hero: [string, string]; font: string; }

/** Canonical live event object a published surface binds to (subset CD reads).
 * Full shape = PublicEventDetail (@v0y4ge/api-contracts). Blocks read this;
 * they never bake their own data. */
export interface PublicEventDetailLike {
  /** Event title — the payment step's order summary reads it. */
  title?: string;
  /** The event's own IANA zone; the countdown reads a bare wall-clock target in it (CC 0251). */
  timezone?: string;
  ticketTiers?: { id: string; name: string; desc?: string; price?: number | string; priceMinor?: number; currency?: string; sold?: number; soldOut?: boolean; kind?: string; requiresPayment?: boolean }[];
  dateLabel?: string;
  /** Multi-day only — one entry per calendar day the event spans. When set
   *  (length > 1) the dates block renders one combined DATE & TIME section
   *  listing every day instead of the single-day Date/Time card pair. */
  days?: { dateLabel: string; timeLabel: string }[];
  venueLabel?: string;
  venueAddress?: string;
  faqItems?: { id?: string; q: string; a: string }[];
  /** Real coordinates from the landing surface payload (data layer). Absent ⇒ the map block keeps its honest placeholder. */
  venueGeo?: { lat?: number; lng?: number; formattedAddress?: string } | null;
  /** StudioRegistrationQuestionItem[] from the canonical event — drives the capture form (Req 5). */
  registrationQuestions?: RegistrationQuestionLike[];
}

/** Read-only mirror of StudioRegistrationQuestionItemSchema (api-contracts
 * events.ts) — the shape the capture form renders from. */
export interface RegistrationQuestionLike {
  id?: string; fieldKey?: string; label?: string; type?: string;
  validation?: string; required?: boolean; help?: string; choices?: string[];
}

/** How a rendered landing behaves:
 *  - "builder": design-time canvas / Preview modal — CTAs inert but labeled.
 *  - "live": the published eyeball/attend surface — real in-place registration.
 *  - "draft": draft preview (publishState 'draft-preview') — visible with a
 *    prominent banner and the registration affordances disabled. */
export type LandingRenderMode = "builder" | "live" | "draft";

export const LANDING_THEMES: LandingTheme[] = [ // ui-config
  { id: "purple",   name: "Indigo",     brand: T.p1,        hero: ["#75ADFA", "#5142C7"], font: "DM Sans" },
  { id: "navy",     name: "Deep navy",  brand: T.tDarkBlue, hero: ["#4981F2", "#14102E"], font: "DM Sans" },
  { id: "amethyst", name: "Amethyst",   brand: T.tPurple,   hero: ["#C18FE8", "#8955F2"], font: "DM Sans" },
  { id: "forest",   name: "Forest",     brand: T.tGreen,    hero: ["#87D23B", "#027D47"], font: "DM Sans" },
  { id: "midnight", name: "Midnight",   brand: "#14102E",   hero: ["#5142C7", "#14102E"], font: "DM Sans" },
];

/** Derive a LandingTheme from a tenant brand kit record — accepts the
 *  TenantThemeContract theme ({ brand, derived }) or a flat BrandingSettings
 *  record (the `brandKit`/`branding` field on the landing surface). Returns
 *  null when no brand color is present so callers can fall back honestly
 *  (voyage#59: never render the design-time preset when the tenant kit exists). */
export function themeFromBrandKit(raw: unknown): LandingTheme | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as { brand?: Record<string, unknown>; derived?: Record<string, unknown> } & Record<string, unknown>;
  const brand = (o.brand && typeof o.brand === "object" ? o.brand : o) as Record<string, unknown>;
  const derived = (o.derived && typeof o.derived === "object" ? o.derived : {}) as Record<string, unknown>;
  const pick = (...vals: unknown[]) => vals.find((v) => typeof v === "string" && v) as string | undefined;
  const brandColor = pick(brand.brandColor, brand.primaryColor, brand.primary, derived.primary);
  if (!brandColor) return null;
  const heroFrom = pick(brand.accentColor, derived.accent) ?? brandColor;
  const heroTo = pick(brand.coverColor, derived.secondary) ?? brandColor;
  const font = pick(brand.bodyFont, brand.headingFont) ?? "DM Sans";
  return { id: "tenant", name: i18nT("landing.builderHelpers.tenantBrand", "Tenant brand"), brand: brandColor, hero: [heroFrom, heroTo], font };
}

/** Minimal event-card shape the hub Events block renders. */
export interface HubEventCard { id: string; title: string; date?: string; location?: string; hue?: string; tag?: string; }

export const LANDING_BLOCKS = [ // ui-config
  { id: "hero",     l: "Hero",       hubOk: true,  defaults: { headline: "", subhead: "", bgKind: "gradient", from: "#75ADFA", to: "#5142C7", solid: "#5142C7", padding: 40, align: "left", showCTA: true, ctaLabel: "Register" } },
  { id: "row",      l: "Columns",    hubOk: true,  defaults: { ratio: "1-1", gap: 20, stackMobile: true, cells: [[], []] } },
  { id: "events",   l: "Events",     hubOk: true,  hubOnly: true, defaults: { title: "Upcoming events", cols: 2, layout: "grid", showFilters: true } },
  { id: "dates",    l: "Date strip", defaults: { date: "", time: "", loc: "", style: "card" } },
  { id: "speakers", l: "Speakers",   defaults: { title: "Speakers", layout: "grid", items: [] } },
  { id: "tickets",  l: "Tickets",    defaults: { title: "Tickets", layout: "list", showSold: false } },
  { id: "agenda",   l: "Agenda",     defaults: { title: "Agenda", style: "timeline", items: [] } },
  { id: "map",      l: "Map",        hubOk: true,  defaults: { title: "Find us", height: 240, address: "" } },
  { id: "faq",      l: "FAQ",        hubOk: true,  defaults: { title: "FAQ", items: [] } },
  { id: "signup",   l: "Register",    defaults: { title: "Register", cta: "Register", showName: true, showCo: false } },
  { id: "text",     l: "Text",       hubOk: true,  defaults: { heading: "", body: "Add your copy here — tell guests what to expect.", align: "left" } },
  { id: "image",    l: "Image",      hubOk: true,  defaults: { imageUrl: "", caption: "", rounded: true, maxW: 0 } },
  { id: "gallery",  l: "Gallery",    hubOk: true,  defaults: { title: "", images: [], cols: 3, motion: "grid" } },
  { id: "video",    l: "Video",      hubOk: true,  defaults: { url: "", caption: "" } },
  { id: "quote",    l: "Quote",      hubOk: true,  defaults: { quote: "", name: "", role: "", photo: "" } },
  { id: "countdown", l: "Countdown", hubOk: true,  defaults: { title: "Starts in", target: "" } },
  { id: "sponsors", l: "Sponsors",   hubOk: true,  defaults: { title: "Sponsors", items: [], grayscale: true, cols: 4, motion: "grid" } },
  { id: "stats",    l: "Stats",      hubOk: true,  defaults: { items: [] } },
  { id: "social",   l: "Social",     hubOk: true,  defaults: { title: "Follow along", smode: "follow", links: {} } },
  { id: "chat",     l: "Chat",        hubOk: true,  defaults: { title: "Chat with us", greeting: "Hi! How can we help?", recipientKind: "staff", recipientId: "", recipientLabel: "", recipientEmail: "", fallbackEmail: "" } },
  { id: "contact",  l: "Contact us",  hubOk: true,  defaults: { title: "Contact us", blurb: "Questions? Reach out and we'll get back to you.", email: "", phone: "", showForm: true } },
];

/** Palette filtered for a build context. "hub" drops single-event blocks
 *  (dates/speakers/tickets/agenda/sign-up) and keeps the Events grid. */
export function landingBlocksFor(context: "landing" | "hub") {
  if (context !== "hub") return LANDING_BLOCKS.filter((b) => !(b as { hubOnly?: boolean }).hubOnly);
  return LANDING_BLOCKS.filter((b) => (b as { hubOk?: boolean }).hubOk);
}

// §3a (2026-07-19): DEFAULT_AGENDA / DEFAULT_FAQ removed — fictional agenda rows
// and FAQ entries rendered whenever a block had no real content. Resolvers now
// return [] and blocks require author input.
// ── Row/columns container helpers ──────────────────────────────────────
// A `row` block nests child blocks inside cfg.cells (LandingBlock[][]) — one
// inner array per column. Storing children in cfg keeps LandingBlock's shape
// unchanged, so blocks_json still round-trips through the typed client and the
// published surface renders rows via the SAME renderer as the builder.
/** Column types that may NOT be placed inside a cell (hero is full-bleed; a row
 *  inside a row is disallowed to keep the tree bounded). */
export const CELL_FORBIDDEN = new Set(["hero", "row"]);

/** Cells normalized to the column count implied by cfg.ratio (pads with empty
 *  columns; merges any overflow into the last column). */
export function getRowCells(cfg: Record<string, unknown>): LandingBlock[][] {
  const ratio = String((cfg as { ratio?: string }).ratio || "1-1");
  const n = ratio.split("-").filter(Boolean).length || 2;
  const raw = Array.isArray((cfg as { cells?: unknown }).cells) ? (cfg as { cells: unknown[] }).cells : [];
  const out: LandingBlock[][] = raw.map((c) => (Array.isArray(c) ? (c as LandingBlock[]) : []));
  while (out.length < n) out.push([]);
  if (out.length > n) { const extra = out.splice(n); extra.forEach((e) => out[n - 1].push(...e)); }
  return out;
}
/** grid-template-columns value derived from cfg.ratio ("2-1" → "2fr 1fr"). */
export function rowGridCols(cfg: Record<string, unknown>): string {
  const parts = String((cfg as { ratio?: string }).ratio || "1-1").split("-").filter(Boolean);
  return (parts.length ? parts : ["1", "1"]).map((s) => `${Number(s) || 1}fr`).join(" ");
}
/** Re-pack a cells array to `n` columns (pad / merge-into-last). */
export function repackCells(cells: LandingBlock[][], n: number): LandingBlock[][] {
  const out = cells.map((c) => [...c]);
  while (out.length < n) out.push([]);
  if (out.length > n) { const extra = out.splice(n); extra.forEach((e) => out[n - 1].push(...e)); }
  return out;
}

// ── Tree-aware block operations (top-level array + nested row cells) ─────
/** The block with `uid`, searched top-level then inside every row's cells. */
export function findBlockDeep(blocks: LandingBlock[], uid?: string | null): LandingBlock | null {
  if (!uid) return null;
  for (const b of blocks) {
    if (b.uid === uid) return b;
    if (b.type === "row") { const hit = findBlockDeep(([] as LandingBlock[]).concat(...getRowCells(b.cfg)), uid); if (hit) return hit; }
  }
  return null;
}
/** The row+cell that contains `uid`, or null when it's a top-level block. */
export function parentRowOf(blocks: LandingBlock[], uid: string): { rowUid: string; cellIdx: number } | null {
  for (const b of blocks) {
    if (b.type !== "row") continue;
    const cells = getRowCells(b.cfg);
    for (let i = 0; i < cells.length; i++) if (cells[i].some((c) => c.uid === uid)) return { rowUid: b.uid, cellIdx: i };
  }
  return null;
}
export function updateCfgDeep(blocks: LandingBlock[], uid: string, patch: Record<string, unknown>): LandingBlock[] {
  return blocks.map((b) => {
    if (b.uid === uid) return { ...b, cfg: { ...b.cfg, ...patch } };
    if (b.type === "row") return { ...b, cfg: { ...b.cfg, cells: getRowCells(b.cfg).map((cell) => updateCfgDeep(cell, uid, patch)) } };
    return b;
  });
}
export function removeBlockDeep(blocks: LandingBlock[], uid: string): LandingBlock[] {
  return blocks.filter((b) => b.uid !== uid).map((b) => b.type === "row" ? { ...b, cfg: { ...b.cfg, cells: getRowCells(b.cfg).map((cell) => removeBlockDeep(cell, uid)) } } : b);
}
export function moveBlockDeep(blocks: LandingBlock[], uid: string, dir: number): LandingBlock[] {
  const i = blocks.findIndex((b) => b.uid === uid);
  if (i >= 0) { const j = i + dir; if (j < 0 || j >= blocks.length) return blocks; const n = [...blocks]; [n[i], n[j]] = [n[j], n[i]]; return n; }
  return blocks.map((b) => b.type === "row" ? { ...b, cfg: { ...b.cfg, cells: getRowCells(b.cfg).map((cell) => {
    const k = cell.findIndex((c) => c.uid === uid); if (k < 0) return cell; const j = k + dir; if (j < 0 || j >= cell.length) return cell; const n = [...cell]; [n[k], n[j]] = [n[j], n[k]]; return n;
  }) } } : b);
}
/** Insert `moving` immediately before `targetUid`, in whichever container the
 *  target lives (top-level or a cell). */
export function insertBeforeDeep(blocks: LandingBlock[], targetUid: string, moving: LandingBlock): LandingBlock[] {
  const i = blocks.findIndex((b) => b.uid === targetUid);
  if (i >= 0) { const n = [...blocks]; n.splice(i, 0, moving); return n; }
  return blocks.map((b) => b.type === "row" ? { ...b, cfg: { ...b.cfg, cells: getRowCells(b.cfg).map((cell) => {
    const k = cell.findIndex((c) => c.uid === targetUid); if (k < 0) return cell; const n = [...cell]; n.splice(k, 0, moving); return n;
  }) } } : b);
}
export function appendToCellDeep(blocks: LandingBlock[], rowUid: string, cellIdx: number, moving: LandingBlock): LandingBlock[] {
  return blocks.map((b) => {
    if (b.uid !== rowUid || b.type !== "row") return b;
    const cells = getRowCells(b.cfg).map((c) => [...c]);
    if (!cells[cellIdx]) cells[cellIdx] = [];
    cells[cellIdx].push(moving);
    return { ...b, cfg: { ...b.cfg, cells } };
  });
}
function freshUid(): string { return `b${Date.now().toString(36)}${((Math.random() * 1e6) | 0).toString(36)}`; }
/** Deep copy of a block with brand-new uids throughout (nested cells too). */
export function cloneBlockDeep(b: LandingBlock): LandingBlock {
  if (b.type === "row") {
    const cells = getRowCells(b.cfg).map((cell) => cell.map(cloneBlockDeep));
    return { uid: freshUid(), type: b.type, cfg: { ...b.cfg, cells } };
  }
  return { uid: freshUid(), type: b.type, cfg: { ...b.cfg } };
}
/** Insert `moving` immediately AFTER `targetUid`, in the target's own container. */
export function insertAfterDeep(blocks: LandingBlock[], targetUid: string, moving: LandingBlock): LandingBlock[] {
  const i = blocks.findIndex((b) => b.uid === targetUid);
  if (i >= 0) { const n = [...blocks]; n.splice(i + 1, 0, moving); return n; }
  return blocks.map((b) => b.type === "row" ? { ...b, cfg: { ...b.cfg, cells: getRowCells(b.cfg).map((cell) => {
    const k = cell.findIndex((c) => c.uid === targetUid); if (k < 0) return cell; const n = [...cell]; n.splice(k + 1, 0, moving); return n;
  }) } } : b);
}

export function resolveAgendaItems(c: Record<string, unknown>) {
  if (Array.isArray(c.items) && (c.items as unknown[]).length) return c.items as { time: string; title: string }[];
  return []; // §3a: no fictional agenda fallback — author input only
}
export function resolveFaqItems(c: Record<string, unknown>) {
  if (Array.isArray(c.items) && (c.items as unknown[]).length) return c.items as { q: string; a: string }[];
  return []; // §3a: no fictional FAQ fallback — author input only
}

// ── Block inspector sub-components ─────────────────────────────────────
export function LabeledInput({ label, value, onChange }: { label: string; value: unknown; onChange: (v: string) => void }) {
  return <Input label={label} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} size="sm"/>;
}
export function LabeledTextarea({ label, value, onChange }: { label: string; value: unknown; onChange: (v: string) => void }) {
  return <Textarea label={label} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} rows={3}/>;
}
export function NativeSelect({ label, value, options, onChange }: { label: string; value: unknown; options: [string, string][]; onChange: (v: string) => void }) {
  return (
    <Dropdown
      label={label}
      value={String(value ?? "")}
      options={options.map(([v, l]) => ({ value: v, label: l }))}
      onChange={onChange}
    />
  );
}
export function NumberInput({ label, value, min = 0, max = 99, onChange }: { label: string; value: unknown; min?: number; max?: number; onChange: (v: number) => void }) {
  return (
    <div>
      <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{label}</label>
      <NumberField value={Number(value) || 0} min={min}
        onChange={(n) => onChange(Math.max(min, Math.min(max, n)))}
        style={{ width: "100%" }}/>
    </div>
  );
}
export function ColorInput({ label, value, onChange }: { label: string; value: unknown; onChange: (v: string) => void }) {
  const v = String(value || "#5142C7");
  return (
    <div>
      <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{label}</label>
      <div data-jrni-component="field-wrap" style={{ display: "flex", alignItems: "center", gap: 6, height: 36, padding: "0 8px", border: `1px solid ${T.n4}`, borderRadius: 4, background: T.white }}>
        {/* ui-raw: native color-picker swatch trigger — no jrni-ui primitive
            exposes the browser's built-in color-picker popover. */}
        <input type="color" value={v} onChange={(e) => onChange(e.target.value)}
          style={{ width: 22, height: 22, padding: 0, border: "none", background: "transparent", cursor: "pointer" }}/>
        {/* ui-raw: borderless hex text field composed into the swatch's own
            outer border — Input always renders its own bordered box, which
            would double-border this single-pill compound control. */}
        <input value={v} onChange={(e) => onChange(e.target.value)}
          style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 12, fontFamily: T.fontMono, color: T.n1 }}/>
      </div>
    </div>
  );
}
// ── ColorWheelField: hue/sat wheel + value slider + hex + favorites ─────
// ui-raw: ColorWheelField is a bespoke hue/saturation-wheel + brightness-slider
// + hex-entry + favorites-swatch popover. jrni-ui's ColorPicker is a different
// shape (saturation/value canvas + separate hue strip, no favorites) — not an
// exact-match swap, so the trigger button, wheel canvas, brightness range
// input, hex text field, and favorite swatch buttons below all stay raw.
export function ColorWheelField({ label, value, defaultValue = "#FFFFFF", hint, onChange }: {
  label: string; value: unknown; defaultValue?: string; hint?: string; onChange: (v: string) => void;
}) {
  const t = useT();
  const current = String(value || defaultValue);
  const [open, setOpen] = useState(false);
  const [hsv, setHsv] = useState<[number, number, number]>(() => hexToHsv(current));
  const [favorites, setFavorites] = useState<string[]>(() => readColorFavorites());
  const [hexText, setHexText] = useState(current);
  const wrapRef = useRef<HTMLDivElement>(null);
  const wheelRef = useRef<HTMLDivElement>(null);

  // Keep internal state in sync when the value changes from outside.
  useEffect(() => { if (!open) { setHsv(hexToHsv(String(value || defaultValue))); setHexText(String(value || defaultValue)); } }, [value, defaultValue, open]);
  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const emit = (h: number, s: number, v: number) => { const hex = hsvToHex(h, s, v); setHexText(hex); onChange(hex); };
  const setFromWheel = (clientX: number, clientY: number) => {
    const el = wheelRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    const dx = clientX - cx, dy = clientY - cy;
    const dist = Math.min(Math.sqrt(dx * dx + dy * dy), r.width / 2);
    let ang = Math.atan2(dy, dx) * 180 / Math.PI; if (ang < 0) ang += 360;
    const s = clamp01(dist / (r.width / 2));
    const next: [number, number, number] = [ang, s, hsv[2] || 1];
    setHsv(next); emit(next[0], next[1], next[2]);
  };
  const onWheelPointer = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setFromWheel(e.clientX, e.clientY);
    const move = (ev: PointerEvent) => setFromWheel(ev.clientX, ev.clientY);
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  const setValue = (v: number) => { const next: [number, number, number] = [hsv[0], hsv[1], clamp01(v)]; setHsv(next); emit(next[0], next[1], next[2]); };
  const applyHexText = (t: string) => {
    setHexText(t);
    let h = t.trim(); if (!h.startsWith("#")) h = "#" + h;
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(h)) { setHsv(hexToHsv(h)); onChange(h.length === 4 ? "#" + h.slice(1).split("").map((c) => c + c).join("") : h); }
  };
  const addFavorite = () => { const hex = hsvToHex(hsv[0], hsv[1], hsv[2]).toUpperCase(); if (favorites.includes(hex)) return; const next = [hex, ...favorites].slice(0, 24); setFavorites(next); writeColorFavorites(next); };
  const removeFavorite = (hex: string) => { const next = favorites.filter((c) => c !== hex); setFavorites(next); writeColorFavorites(next); };
  const pickFavorite = (hex: string) => { setHsv(hexToHsv(hex)); setHexText(hex); onChange(hex); };

  const markerColor = hsv[2] > 0.6 && hsv[1] < 0.5 ? "#000" : "#fff";
  const wheelPx = 168, radius = wheelPx / 2;
  const mx = radius + Math.cos(hsv[0] * Math.PI / 180) * hsv[1] * radius;
  const my = radius + Math.sin(hsv[0] * Math.PI / 180) * hsv[1] * radius;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{label}</label>
      {/* ui-raw: colour-field trigger (swatch + hex + caret) — ColorPicker's pattern (group 10) */}
      <button type="button" onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", height: 36, padding: "0 8px", border: `1px solid ${open ? T.p1 : T.n4}`, borderRadius: 4, background: T.white, cursor: "pointer", fontFamily: T.font }}>
        <span style={{ width: 20, height: 20, borderRadius: 4, background: current, border: `1px solid ${alpha(T.n1, 15)}`, flexShrink: 0 }}/>
        <span style={{ flex: 1, textAlign: "left", fontSize: 12, fontFamily: T.fontMono, color: T.n1 }}>{current.toUpperCase()}</span>
        <Icon.chev size={12} dir={open ? "up" : "down"} style={{ color: T.soft }}/>
      </button>
      {hint ? <div style={{ fontSize: 11, color: T.soft, marginTop: 4 }}>{hint}</div> : null}
      {open ? (
        <div style={{ position: "absolute", zIndex: 50, top: "calc(100% + 6px)", left: 0, width: 220, background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 16px 40px rgba(15,18,25,.22)", padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Wheel */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div ref={wheelRef} onPointerDown={onWheelPointer}
              style={{ position: "relative", width: wheelPx, height: wheelPx, borderRadius: "50%", cursor: "crosshair", touchAction: "none",
                background: `radial-gradient(circle at center, #fff 0%, rgba(255,255,255,0) 70%), conic-gradient(from 90deg, #f00 0deg, #ff0 60deg, #0f0 120deg, #0ff 180deg, #00f 240deg, #f0f 300deg, #f00 360deg)`,
                boxShadow: `inset 0 0 0 1px ${alpha(T.n1, 12)}` }}>
              {/* value overlay darkens the whole wheel */}
              <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "#000", opacity: 1 - hsv[2], pointerEvents: "none" }}/>
              <div style={{ position: "absolute", width: 14, height: 14, borderRadius: "50%", left: mx - 7, top: my - 7, border: `2px solid ${markerColor}`, boxShadow: "0 0 0 1px rgba(0,0,0,.35)", pointerEvents: "none" }}/>
            </div>
          </div>
          {/* Value / brightness slider */}
          <div>
            <div style={{ fontSize: 11, color: T.soft, marginBottom: 4 }}>{t("landing.builderHelpers.brightness", "Brightness")}</div>
            <input type="range" min={0} max={100} value={Math.round(hsv[2] * 100)} onChange={(e) => setValue(Number(e.target.value) / 100)}
              style={{ width: "100%", accentColor: hsvToHex(hsv[0], hsv[1], 1) }}/>
          </div>
          {/* Hex + save */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div data-jrni-component="field-wrap" style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, height: 32, padding: "0 8px", border: `1px solid ${T.n4}`, borderRadius: 4 }}>
              <span style={{ width: 16, height: 16, borderRadius: 3, background: hexText, border: `1px solid ${alpha(T.n1, 15)}` }}/>
              <input value={hexText} onChange={(e) => applyHexText(e.target.value)} spellCheck={false}
                style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 12, fontFamily: T.fontMono, color: T.n1, minWidth: 0 }}/>
            </div>
            <Btn size="sm" variant="secondary" icon={<Icon.plus size={12}/>} title={t("landing.builderHelpers.saveToFavorites", "Save to favorites")} onClick={addFavorite}>
              {t("landing.builderHelpers.save", "Save")}</Btn>
          </div>
          {/* Favorites */}
          <div>
            <div style={{ fontSize: 11, color: T.soft, marginBottom: 6 }}>{t("landing.builderHelpers.favorites", "Favorites")}</div>
            {favorites.length ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {favorites.map((hex) => (
                  /* ui-raw: favourite colour swatch — ColorPicker's pattern (group 10) */
                  <button key={hex} type="button" title={hex} onClick={() => pickFavorite(hex)}
                    onContextMenu={(e) => { e.preventDefault(); removeFavorite(hex); }}
                    style={{ width: 22, height: 22, borderRadius: 5, background: hex, border: current.toUpperCase() === hex.toUpperCase() ? `2px solid ${T.n1}` : `1px solid ${alpha(T.n1, 15)}`, cursor: "pointer", padding: 0 }}/>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: T.soft }}>{t("landing.builderHelpers.noFavoritesYetPickAColorAndHitSaveRightClickA", "No favorites yet — pick a color and hit Save. Right-click a swatch to remove.")}</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function LabeledSlider({ label, value, min = 0, max = 1, step = 0.05, defaultValue = 1, onChange, format }: { label: string; value: unknown; min?: number; max?: number; step?: number; defaultValue?: number; onChange: (v: number) => void; format?: (n: number) => string }) {
  const v = value == null || value === "" ? defaultValue : Number(value);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <label style={{ fontSize: 13, color: T.n1, fontWeight: 500 }}>{label}</label>
        <span style={{ fontSize: 12, color: T.soft, fontFamily: T.fontMono }}>{format ? format(v) : v}</span>
      </div>
      {/* ui-raw: plain native range input paired with an inline value label
          this component already renders itself; jrni-ui's Slider owns its
          own custom track/thumb + optional label — swapping would double
          the value display and change the compact single-line layout. */}
      <input type="range" min={min} max={max} step={step} value={v} onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: T.p1 }}/>
    </div>
  );
}
export function ToggleRow({ label, on, onChange }: { label: string; on: unknown; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0", cursor: "pointer" }}
      onClick={() => onChange(!on)}>
      <Toggle on={!!on}/>
      <span style={{ fontSize: 13, color: T.n1, fontWeight: 500 }}>{label}</span>
    </label>
  );
}
export function ListEditor({ label, items, columns, blank, addLabel, onChange }: {
  label?: string;
  items: Record<string, string>[];
  columns: { key: string; placeholder?: string; type?: string; width?: number }[];
  blank: Record<string, string>;
  addLabel?: string;
  onChange: (items: Record<string, string>[]) => void;
}) {
  const t = useT();
  const list = Array.isArray(items) ? items : [];
  const setItem = (i: number, key: string, val: string) => onChange(list.map((it, j) => j === i ? { ...it, [key]: val } : it));
  const remove  = (i: number) => onChange(list.filter((_, j) => j !== i));
  const add     = () => onChange([...list, { ...blank }]);
  const move    = (i: number, dir: number) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const fieldBase: React.CSSProperties = { width: "100%", border: `1px solid ${T.n4}`, borderRadius: 4, background: T.white, color: T.n1, fontSize: 13, fontFamily: T.font, boxSizing: "border-box" };
  return (
    <div>
      {label ? <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 6 }}>{label}</label> : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {list.map((it, i) => (
          <div key={i} style={{ border: `1px solid ${T.border}`, borderRadius: 6, padding: 8, display: "flex", flexDirection: "column", gap: 6, background: T.n6 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 11, color: T.soft, fontWeight: 600 }}>{t("landing.builderHelpers.item", "Item")}{" "}{i + 1}</span>
              <div style={{ display: "flex", gap: 2 }}>
                {/* ui-raw: 22px borderless nudge control in a dense inline row (miniBtn) — IconBtn is 32/40/48 with a border and chrome shadow; needs the borderless treatment (capability 1) AND a sub-32 step */}
                <button onClick={() => move(i, -1)} title={t("landing.builderHelpers.moveUp", "Move up")}   disabled={i === 0} style={{ ...miniBtn as object, opacity: i === 0 ? 0.35 : 1 } as React.CSSProperties}><Icon.chev dir="up" size={11}/></button>
                {/* ui-raw: 22px borderless nudge control in a dense inline row (miniBtn) — IconBtn is 32/40/48 with a border and chrome shadow; needs the borderless treatment (capability 1) AND a sub-32 step */}
                <button onClick={() => move(i, +1)} title={t("landing.builderHelpers.moveDown", "Move down")} disabled={i === list.length - 1} style={{ ...miniBtn as object, opacity: i === list.length - 1 ? 0.35 : 1 } as React.CSSProperties}><Icon.chev size={11}/></button>
                {/* ui-raw: 22px borderless nudge control in a dense inline row (miniBtn) — IconBtn is 32/40/48 with a border and chrome shadow; needs the borderless treatment (capability 1) AND a sub-32 step */}
                <button onClick={() => remove(i)} title={t("common.actions.remove", "Remove")} style={{ ...miniBtn as object, color: T.red1 } as React.CSSProperties}><Icon.trash size={11}/></button>
              </div>
            </div>
            {columns.map(col => col.type === "textarea" ? (
              <textarea key={col.key} value={it[col.key] || ""} placeholder={col.placeholder} rows={2}
                onChange={(e) => setItem(i, col.key, e.target.value)}
                style={{ ...fieldBase, padding: "6px 8px", resize: "vertical" as "vertical", lineHeight: 1.4 }}/>
            ) : (
              <input key={col.key} value={it[col.key] || ""} placeholder={col.placeholder}
                onChange={(e) => setItem(i, col.key, e.target.value)}
                style={{ ...fieldBase, height: 32, padding: "0 8px" }}/>
            ))}
          </div>
        ))}
        {/* ui-raw: dashed "add new row" placeholder affordance — visually
            distinct from Btn's solid-border secondary treatment on purpose
            (signals "empty slot", not a standard action button). */}
        <button onClick={add} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 10px", border: `1px dashed ${T.n4}`, borderRadius: 6, background: T.white, color: T.p1, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
          <Icon.plus size={13}/> {addLabel || t("landing.landingBuilderHelpers.addItem", "Add item")}
        </button>
      </div>
    </div>
  );
}
// ui-raw: simple drag/drop-only image dropzone (thumbnail preview + hidden
// file input + remove button). jrni-ui's ImageDropField is close but adds a
// crop modal + URL-entry field this callback contract doesn't expect —
// swapping would change behavior, not just the rendering primitive.
export function LandingHeroImage({ cfg, onChange }: { cfg: Record<string, unknown>; onChange: (p: Record<string, unknown>) => void }) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const has = !!cfg.imageUrl;
  const handleFile = (file: File | null | undefined) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => onChange({ bgKind: "image", imageUrl: reader.result });
    reader.readAsDataURL(file);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {has ? (
        <div style={{ position: "relative", height: 96, borderRadius: 8, overflow: "hidden", background: `url(${cfg.imageUrl}) center/cover`, border: `1px solid ${T.border}` }}>
          {/* ui-raw: remove-overlay on an uploaded image (dark scrim circle, white glyph) — ImageDropField owns this affordance; not an IconBtn treatment */}
          <button onClick={() => onChange({ imageUrl: "" })} style={{ position: "absolute", top: 6, right: 6, width: 24, height: 24, borderRadius: 999, border: "none", background: "rgba(0,0,0,0.55)", color: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Icon.x size={12}/>
          </button>
        </div>
      ) : null}
      <div onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
        style={{ border: `1.5px dashed ${dragOver ? T.p1 : T.n4}`, borderRadius: 8, padding: "14px 12px", textAlign: "center", cursor: "pointer", background: dragOver ? T.p4 : T.n6, fontSize: 12, color: T.soft }}>
        <Icon.upload size={16} style={{ color: dragOver ? T.p1 : T.soft }}/>
        <div style={{ marginTop: 4 }}>{has ? t("landing.builderHelpers.text", "Replace image —") : t("landing.builderHelpers.text2", "Drag image, or")}<span style={{ color: T.p1, fontWeight: 600 }}>{t("landing.builderHelpers.browse", "browse")}</span></div>
        <div style={{ fontSize: 11, marginTop: 2 }}>{t("landing.builderHelpers.pngOrJpg1600900Recommended", "PNG or JPG · 1600×900 recommended")}</div>
        <input ref={fileRef} type="file" accept="image/*" onChange={(e) => handleFile(e.target.files?.[0])} style={{ display: "none" }}/>
      </div>
    </div>
  );
}

// ── Speakers: social platforms, glyphs, photo & list editor ─────────────
export interface Speaker { id?: string; photo?: string; name?: string; title?: string; bio?: string; socials?: Record<string, string>; }

export const SOCIAL_PLATFORMS: { id: string; label: string; labelKey: string; placeholder: string; placeholderKey: string; path?: string; useGlobe?: boolean }[] = [
  { id: "linkedin",  labelKey: "landing.builderHelpers.linkedin", get label() { return i18nT("landing.builderHelpers.linkedin", "LinkedIn"); },  placeholderKey: "landing.builderHelpers.linkedinComIn", get placeholder() { return i18nT("landing.builderHelpers.linkedinComIn", "linkedin.com/in/…"); }, path: "M4.98 3.5A2.5 2.5 0 112.48 6 2.5 2.5 0 014.98 3.5zM.5 8.2h4.96V24H.5zM8.2 8.2h4.75v2.16h.07c.66-1.2 2.28-2.46 4.69-2.46 5.02 0 5.95 3.19 5.95 7.34V24h-4.96v-6.98c0-1.66-.03-3.8-2.36-3.8-2.36 0-2.72 1.8-2.72 3.68V24H8.2z" },
  { id: "x",         labelKey: "landing.builderHelpers.x", get label() { return i18nT("landing.builderHelpers.x", "X"); },         placeholderKey: "landing.builderHelpers.xCom", get placeholder() { return i18nT("landing.builderHelpers.xCom", "x.com/…"); },           path: "M18.9 2H22l-7.5 8.6L23 22h-6.9l-5.4-7-6.2 7H1.4l8-9.2L1 2h7l4.9 6.5L18.9 2zm-2.4 18h1.9L7.1 4H5.1z" },
  { id: "instagram", labelKey: "landing.builderHelpers.instagram", get label() { return i18nT("landing.builderHelpers.instagram", "Instagram"); }, placeholderKey: "landing.builderHelpers.instagramCom", get placeholder() { return i18nT("landing.builderHelpers.instagramCom", "instagram.com/…"); },   path: "M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.05.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 01-1.38-.9 3.7 3.7 0 01-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.18.25-1.81.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.43-.16 1.06-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2zm0 1.8c-3.15 0-3.5.01-4.74.07-1.14.05-1.76.24-2.17.4-.55.22-.94.47-1.35.88-.41.41-.66.8-.88 1.35-.16.41-.35 1.03-.4 2.17C2.41 10.5 2.4 10.85 2.4 12s.01 1.5.07 2.74c.05 1.14.24 1.76.4 2.17.22.55.47.94.88 1.35.41.41.8.66 1.35.88.41.16 1.03.35 2.17.4 1.24.06 1.59.07 4.74.07s3.5-.01 4.74-.07c1.14-.05 1.76-.24 2.17-.4.55-.22.94-.47 1.35-.88.41-.41.66-.8.88-1.35.16-.41.35-1.03.4-2.17.06-1.24.07-1.59.07-2.74s-.01-1.5-.07-2.74c-.05-1.14-.24-1.76-.4-2.17a3.6 3.6 0 00-.88-1.35 3.6 3.6 0 00-1.35-.88c-.41-.16-1.03-.35-2.17-.4C15.5 4.01 15.15 4 12 4zm0 3.06A4.94 4.94 0 1116.94 12 4.94 4.94 0 0112 7.06zm0 8.14A3.2 3.2 0 1015.2 12 3.2 3.2 0 0012 15.2zm5.14-8.34a1.15 1.15 0 11-1.15-1.15 1.15 1.15 0 011.15 1.15z" },
  { id: "facebook",  labelKey: "landing.builderHelpers.facebook", get label() { return i18nT("landing.builderHelpers.facebook", "Facebook"); },  placeholderKey: "landing.builderHelpers.facebookCom", get placeholder() { return i18nT("landing.builderHelpers.facebookCom", "facebook.com/…"); },     path: "M24 12a12 12 0 10-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.66 4.53-4.66 1.31 0 2.68.23 2.68.23v2.95H15.8c-1.49 0-1.95.93-1.95 1.87V12h3.32l-.53 3.47h-2.79v8.38A12 12 0 0024 12z" },
  { id: "youtube",   labelKey: "landing.builderHelpers.youtube", get label() { return i18nT("landing.builderHelpers.youtube", "YouTube"); },   placeholderKey: "landing.builderHelpers.youtubeCom", get placeholder() { return i18nT("landing.builderHelpers.youtubeCom", "youtube.com/@…"); },     path: "M23.5 6.2a3 3 0 00-2.11-2.13C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.39.52A3 3 0 00.5 6.2 31.4 31.4 0 000 12a31.4 31.4 0 00.5 5.8 3 3 0 002.11 2.13c1.89.52 9.39.52 9.39.52s7.5 0 9.39-.52a3 3 0 002.11-2.13A31.4 31.4 0 0024 12a31.4 31.4 0 00-.5-5.8zM9.6 15.57V8.43L15.82 12z" },
  { id: "tiktok",    labelKey: "landing.builderHelpers.tiktok", get label() { return i18nT("landing.builderHelpers.tiktok", "TikTok"); },    placeholderKey: "landing.builderHelpers.tiktokCom", get placeholder() { return i18nT("landing.builderHelpers.tiktokCom", "tiktok.com/@…"); },      path: "M16.6 5.82a4.28 4.28 0 01-1.06-2.82h-3.3v13.2a2.59 2.59 0 11-2.6-2.59c.27 0 .53.04.77.11V10.4a5.9 5.9 0 00-.77-.05 5.9 5.9 0 105.9 5.9V9.01a7.53 7.53 0 004.4 1.4V7.1a4.28 4.28 0 01-3.34-1.28z" },
  { id: "website",   labelKey: "landing.builderHelpers.website", get label() { return i18nT("landing.builderHelpers.website", "Website"); },   placeholderKey: "landing.builderHelpers.yoursiteCom", get placeholder() { return i18nT("landing.builderHelpers.yoursiteCom", "yoursite.com"); },       useGlobe: true },
];

export function SocialGlyph({ id, size = 14 }: { id: string; size?: number }) {
  const p = SOCIAL_PLATFORMS.find((s) => s.id === id);
  if (!p || p.useGlobe) return <Icon.globe size={size}/>;
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={p.path}/></svg>;
}

export function initialsFrom(name?: string): string {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "SP";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

// §3a (2026-07-19): DEFAULT_SPEAKERS + the `Speaker ${i+1}` count-path removed —
// both invented identities/employers. Speakers render only from author input.
export function resolveSpeakerItems(c: Record<string, unknown>): Speaker[] {
  if (Array.isArray(c.items) && (c.items as unknown[]).length) return c.items as Speaker[];
  return [];
}

// ui-raw: same simple drag/drop-only pattern as LandingHeroImage, sized as a
// small circular avatar drop target — no jrni-ui exact match (see note there).
function SpeakerPhoto({ speaker, onChange }: { speaker: Speaker; onChange: (photo: string) => void }) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const handleFile = (file: File | null | undefined) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.readAsDataURL(file);
  };
  return (
    <div onClick={() => fileRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
      title={t("landing.builderHelpers.uploadPhoto", "Upload photo")}
      style={{ position: "relative", width: 52, height: 52, borderRadius: 8, flexShrink: 0, cursor: "pointer", overflow: "hidden",
        border: `1.5px dashed ${dragOver ? T.p1 : T.n4}`, background: speaker.photo ? `url(${speaker.photo}) center/cover` : (dragOver ? T.p4 : T.n6),
        display: "flex", alignItems: "center", justifyContent: "center", color: T.soft }}>
      {speaker.photo ? (
        /* ui-raw: remove-overlay on an uploaded image (dark scrim circle, white glyph) — ImageDropField owns this affordance; not an IconBtn treatment */
        <button onClick={(e) => { e.stopPropagation(); onChange(""); }} title={t("landing.builderHelpers.removePhoto", "Remove photo")}
          style={{ position: "absolute", top: 2, right: 2, width: 16, height: 16, borderRadius: 999, border: "none", background: "rgba(0,0,0,.55)", color: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.x size={9}/></button>
      ) : <Icon.upload size={16}/>}
      <input ref={fileRef} type="file" accept="image/*" onChange={(e) => handleFile(e.target.files?.[0])} style={{ display: "none" }}/>
    </div>
  );
}

export function SpeakerListEditor({ items, onChange }: { items: Speaker[]; onChange: (items: Speaker[]) => void }) {
  const t = useT();
  const list = Array.isArray(items) ? items : [];
  const patch = (i: number, p: Partial<Speaker>) => onChange(list.map((s, j) => j === i ? { ...s, ...p } : s));
  const patchSocial = (i: number, key: string, val: string) => patch(i, { socials: { ...(list[i].socials || {}), [key]: val } });
  const remove = (i: number) => onChange(list.filter((_, j) => j !== i));
  const move = (i: number, dir: number) => { const j = i + dir; if (j < 0 || j >= list.length) return; const next = [...list]; [next[i], next[j]] = [next[j], next[i]]; onChange(next); };
  const add = () => onChange([...list, { id: `sp${Date.now()}`, name: "", title: "", bio: "", socials: {} }]);
  const fieldBase: React.CSSProperties = { width: "100%", border: `1px solid ${T.n4}`, borderRadius: 4, background: T.white, color: T.n1, fontSize: 13, fontFamily: T.font, boxSizing: "border-box" };
  return (
    <div>
      <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 6 }}>{t("landing.builderHelpers.speakers", "Speakers")}</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {list.map((sp, i) => (
          <div key={sp.id || i} style={{ border: `1px solid ${T.border}`, borderRadius: 8, padding: 10, display: "flex", flexDirection: "column", gap: 8, background: T.n6 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 11, color: T.soft, fontWeight: 700 }}>{t("landing.builderHelpers.speaker", "Speaker")}{" "}{i + 1}</span>
              <div style={{ display: "flex", gap: 2 }}>
                {/* ui-raw: 22px borderless nudge control in a dense inline row (miniBtn) — IconBtn is 32/40/48 with a border and chrome shadow; needs the borderless treatment (capability 1) AND a sub-32 step */}
                <button onClick={() => move(i, -1)} title={t("landing.builderHelpers.moveUp", "Move up")} disabled={i === 0} style={{ ...miniBtn as object, opacity: i === 0 ? 0.35 : 1 } as React.CSSProperties}><Icon.chev dir="up" size={11}/></button>
                {/* ui-raw: 22px borderless nudge control in a dense inline row (miniBtn) — IconBtn is 32/40/48 with a border and chrome shadow; needs the borderless treatment (capability 1) AND a sub-32 step */}
                <button onClick={() => move(i, +1)} title={t("landing.builderHelpers.moveDown", "Move down")} disabled={i === list.length - 1} style={{ ...miniBtn as object, opacity: i === list.length - 1 ? 0.35 : 1 } as React.CSSProperties}><Icon.chev size={11}/></button>
                {/* ui-raw: 22px borderless nudge control in a dense inline row (miniBtn) — IconBtn is 32/40/48 with a border and chrome shadow; needs the borderless treatment (capability 1) AND a sub-32 step */}
                <button onClick={() => remove(i)} title={t("common.actions.remove", "Remove")} style={{ ...miniBtn as object, color: T.red1 } as React.CSSProperties}><Icon.trash size={11}/></button>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <SpeakerPhoto speaker={sp} onChange={(photo) => patch(i, { photo })}/>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                <input value={sp.name || ""} placeholder={t("landing.builderHelpers.fullName", "Full name")} onChange={(e) => patch(i, { name: e.target.value })} style={{ ...fieldBase, height: 32, padding: "0 8px" }}/>
                <input value={sp.title || ""} placeholder={t("landing.builderHelpers.titleCompany", "Title · Company")} onChange={(e) => patch(i, { title: e.target.value })} style={{ ...fieldBase, height: 32, padding: "0 8px" }}/>
              </div>
            </div>
            <textarea value={sp.bio || ""} placeholder={t("landing.builderHelpers.shortBioUpToAParagraph", "Short bio (up to a paragraph)")} rows={3} maxLength={480}
              onChange={(e) => patch(i, { bio: e.target.value })} style={{ ...fieldBase, padding: "6px 8px", resize: "vertical", lineHeight: 1.45 }}/>
            <div>
              <div style={{ fontSize: 11, color: T.soft, fontWeight: 600, marginBottom: 4 }}>{t("landing.builderHelpers.socialLinks", "Social links")}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                {SOCIAL_PLATFORMS.map((p) => (
                  <div key={p.id} title={p.label} data-jrni-component="field-wrap" style={{ display: "flex", alignItems: "center", gap: 6, height: 30, padding: "0 8px", border: `1px solid ${T.n4}`, borderRadius: 4, background: T.white }}>
                    <span style={{ color: T.n2, flexShrink: 0, display: "inline-flex" }}><SocialGlyph id={p.id} size={13}/></span>
                    <input value={sp.socials?.[p.id] || ""} placeholder={p.placeholder} onChange={(e) => patchSocial(i, p.id, e.target.value)}
                      style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 12, fontFamily: T.font, color: T.n1, minWidth: 0 }}/>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
        {/* ui-raw: dashed "add new row" placeholder affordance — same
            intentional non-Btn treatment as ListEditor's add button. */}
        <button onClick={add} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 10px", border: `1px dashed ${T.n4}`, borderRadius: 6, background: T.white, color: T.p1, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
          <Icon.plus size={13}/> {t("landing.builderHelpers.addSpeaker", "Add speaker")}</button>
      </div>
    </div>
  );
}

// ── Extra block editors + helpers (text / image / gallery / video / quote /
//    countdown / cta / sponsors / stats / social) ───────────────────────────
/** Generic single-image drag/drop uploader used by image, quote, gallery and
 *  sponsor editors. Writes a data URL (or "" when cleared) via onChange. */
// ui-raw: same simple drag/drop-only pattern as LandingHeroImage — no jrni-ui
// exact match (see note there).
export function MiniImageDrop({ url, onChange, height = 96, hint }: { url?: unknown; onChange: (v: string) => void; height?: number; hint?: string }) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const has = !!url;
  const handleFile = (file: File | null | undefined) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.readAsDataURL(file);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {has ? (
        <div style={{ position: "relative", height, borderRadius: 8, overflow: "hidden", background: `url(${url}) center/cover`, border: `1px solid ${T.border}` }}>
          {/* ui-raw: remove-overlay on an uploaded image (dark scrim circle, white glyph) — ImageDropField owns this affordance; not an IconBtn treatment */}
          <button onClick={() => onChange("")} title={t("landing.builderHelpers.removeImage", "Remove image")} style={{ position: "absolute", top: 6, right: 6, width: 24, height: 24, borderRadius: 999, border: "none", background: "rgba(0,0,0,0.55)", color: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.x size={12}/></button>
        </div>
      ) : null}
      <div onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
        style={{ border: `1.5px dashed ${dragOver ? T.p1 : T.n4}`, borderRadius: 8, padding: "12px", textAlign: "center", cursor: "pointer", background: dragOver ? T.p4 : T.n6, fontSize: 12, color: T.soft }}>
        <Icon.upload size={15} style={{ color: dragOver ? T.p1 : T.soft }}/>
        <div style={{ marginTop: 4 }}>{has ? t("landing.builderHelpers.text3", "Replace —") : t("landing.builderHelpers.text2", "Drag image, or")}<span style={{ color: T.p1, fontWeight: 600 }}>{t("landing.builderHelpers.browse", "browse")}</span></div>
        {hint ? <div style={{ fontSize: 11, marginTop: 2 }}>{hint}</div> : null}
        <input ref={fileRef} type="file" accept="image/*" onChange={(e) => handleFile(e.target.files?.[0])} style={{ display: "none" }}/>
      </div>
    </div>
  );
}

// ui-raw: native HTML5 drag-to-reorder rows (draggable + onDragStart/Over/Drop)
// with a miniBtn-style remove action — no jrni-ui reorderable-list primitive
// covers drag-and-drop reordering of raw image thumbnails.
export function GalleryEditor({ images, onChange }: { images: string[]; onChange: (v: string[]) => void }) {
  const t = useT();
  const list = Array.isArray(images) ? images : [];
  const dragIdx = useRef<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const reorder = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0) return;
    const next = [...list]; const [it] = next.splice(from, 1); next.splice(to, 0, it); onChange(next);
  };
  return (
    <div>
      <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 6 }}>{t("landing.builderHelpers.images", "Images")}<span style={{ color: T.soft, fontWeight: 400 }}>{t("landing.builderHelpers.dragToReorder8a0388c2", "· drag to reorder")}</span></label>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {list.map((src, i) => (
          <div key={i} draggable
            onDragStart={() => { dragIdx.current = i; }}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
            onDrop={(e) => { e.preventDefault(); if (dragIdx.current != null) reorder(dragIdx.current, i); dragIdx.current = null; setOverIdx(null); }}
            onDragEnd={() => { dragIdx.current = null; setOverIdx(null); }}
            style={{ display: "flex", gap: 8, alignItems: "center", border: `1px solid ${overIdx === i ? T.p1 : T.border}`, borderRadius: 6, padding: 6, background: overIdx === i ? T.p4 : T.n6, cursor: "grab" }}>
            <span style={{ color: T.n4, fontSize: 14, lineHeight: 1, flexShrink: 0, userSelect: "none" }} title={t("landing.builderHelpers.dragToReorder", "Drag to reorder")}>{"\u2261"}</span>
            <div style={{ width: 44, height: 44, borderRadius: 6, background: `url(${src}) center/cover`, border: `1px solid ${T.border}`, flexShrink: 0 }}/>
            <span style={{ flex: 1, fontSize: 12, color: T.soft }}>{t("landing.builderHelpers.image", "Image")}{" "}{i + 1}</span>
            {/* ui-raw: 22px borderless nudge control in a dense inline row (miniBtn) — IconBtn is 32/40/48 with a border and chrome shadow; needs the borderless treatment (capability 1) AND a sub-32 step */}
            <button onClick={() => onChange(list.filter((_, j) => j !== i))} title={t("common.actions.remove", "Remove")} style={{ ...miniBtn as object, color: T.red1 } as React.CSSProperties}><Icon.trash size={11}/></button>
          </div>
        ))}
        <MiniImageDrop onChange={(v) => v && onChange([...list, v])} height={72} hint={t("landing.builderHelpers.hint", "Adds to the gallery")}/>
      </div>
    </div>
  );
}

// ui-raw: prev/next arrows are absolutely-positioned, semi-transparent
// overlays floating on top of a horizontal image scroller — a different
// visual treatment than IconBtn's opaque bordered/shadowed default, and this
// live carousel is themed per event (see the RenderedBlock note below).
function HScroller({ children, motion, mode }: { children: React.ReactNode; motion: string; mode: LandingRenderMode }) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (motion !== "auto" || mode !== "live") return;
    const el = ref.current; if (!el) return;
    let raf = 0; let last = performance.now();
    const tick = (t: number) => {
      const dt = t - last; last = t;
      if (!paused && el.scrollWidth > el.clientWidth) {
        el.scrollLeft += dt * 0.04;
        if (el.scrollLeft >= el.scrollWidth - el.clientWidth - 1) el.scrollLeft = 0;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [motion, mode, paused]);
  const scroll = (dir: number) => ref.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  const arrow = (dir: -1 | 1): React.CSSProperties => ({ position: "absolute", top: "50%", [dir === -1 ? "left" : "right"]: 6, transform: "translateY(-50%)", zIndex: 2, width: 34, height: 34, borderRadius: 999, border: `1px solid ${T.border}`, background: "rgba(255,255,255,0.92)", color: T.n1, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,.12)" });
  return (
    <div style={{ position: "relative" }} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div ref={ref} style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4, alignItems: "stretch" }}>
        {children}
      </div>
      {motion === "buttons" ? (
        <>
          {/* ui-raw: remove-overlay on an uploaded image (dark scrim circle, white glyph) — ImageDropField owns this affordance; not an IconBtn treatment */}
          <button onClick={() => scroll(-1)} title={t("landing.builderHelpers.previous", "Previous")} style={arrow(-1)}><Icon.chev dir="left" size={16}/></button>
          {/* ui-raw: remove-overlay on an uploaded image (dark scrim circle, white glyph) — ImageDropField owns this affordance; not an IconBtn treatment */}
          <button onClick={() => scroll(1)} title={t("landing.builderHelpers.next", "Next")} style={arrow(1)}><Icon.chev dir="right" size={16}/></button>
        </>
      ) : null}
    </div>
  );
}

export interface Sponsor { name?: string; logo?: string; bio?: string; socials?: Record<string, string> }
export function SponsorsEditor({ items, onChange }: { items: Sponsor[]; onChange: (v: Sponsor[]) => void }) {
  const t = useT();
  const list = Array.isArray(items) ? items : [];
  const patch = (i: number, p: Partial<Sponsor>) => onChange(list.map((s, j) => j === i ? { ...s, ...p } : s));
  const patchSocial = (i: number, key: string, val: string) => patch(i, { socials: { ...(list[i].socials || {}), [key]: val } });
  const dragIdx = useRef<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const reorder = (from: number, to: number) => { if (from === to || from < 0 || to < 0) return; const next = [...list]; const [it] = next.splice(from, 1); next.splice(to, 0, it); onChange(next); };
  return (
    <div>
      <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 6 }}>{t("landing.builderHelpers.sponsors", "Sponsors")}<span style={{ color: T.soft, fontWeight: 400 }}>{t("landing.builderHelpers.dragToReorder8a0388c2", "· drag to reorder")}</span></label>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {list.map((sp, i) => (
          <div key={i}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
            onDrop={(e) => { e.preventDefault(); if (dragIdx.current != null) reorder(dragIdx.current, i); dragIdx.current = null; setOverIdx(null); }}
            style={{ border: `1px solid ${overIdx === i ? T.p1 : T.border}`, borderRadius: 6, padding: 8, display: "flex", flexDirection: "column", gap: 6, background: overIdx === i ? T.p4 : T.n6 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span draggable onDragStart={() => { dragIdx.current = i; }} onDragEnd={() => { dragIdx.current = null; setOverIdx(null); }} title={t("landing.builderHelpers.dragToReorder", "Drag to reorder")} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "grab" }}>
                <span style={{ color: T.n4, fontSize: 14, lineHeight: 1, userSelect: "none" }}>{"\u2261"}</span>
                <span style={{ fontSize: 11, color: T.soft, fontWeight: 600 }}>{t("landing.builderHelpers.sponsor", "Sponsor")}{" "}{i + 1}</span>
              </span>
              {/* ui-raw: 22px borderless nudge control in a dense inline row (miniBtn) — needs the borderless treatment (capability 1) AND a sub-32 step */}
              <button onClick={() => onChange(list.filter((_, j) => j !== i))} title={t("common.actions.remove", "Remove")} style={{ ...miniBtn as object, color: T.red1 } as React.CSSProperties}><Icon.trash size={11}/></button>
            </div>
            <input value={sp.name || ""} placeholder={t("landing.builderHelpers.nameUsedAsAltText", "Name (used as alt text)")} onChange={(e) => patch(i, { name: e.target.value })}
              style={{ width: "100%", height: 32, padding: "0 8px", border: `1px solid ${T.n4}`, borderRadius: 4, background: T.white, color: T.n1, fontSize: 13, fontFamily: T.font, boxSizing: "border-box" }}/>
            <textarea value={sp.bio || ""} placeholder={t("landing.builderHelpers.shortDescriptionBioOptional", "Short description / bio (optional)")} rows={2} maxLength={360}
              onChange={(e) => patch(i, { bio: e.target.value })}
              style={{ width: "100%", padding: "6px 8px", border: `1px solid ${T.n4}`, borderRadius: 4, background: T.white, color: T.n1, fontSize: 13, fontFamily: T.font, resize: "vertical", lineHeight: 1.45, boxSizing: "border-box" }}/>
            <MiniImageDrop url={sp.logo} onChange={(v) => patch(i, { logo: v })} height={64} hint={t("landing.builderHelpers.hint2", "Logo — PNG with transparency ideal")}/>
            <div>
              <div style={{ fontSize: 11, color: T.soft, fontWeight: 600, marginBottom: 4 }}>{t("landing.builderHelpers.socialLinks", "Social links")}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                {SOCIAL_PLATFORMS.map((p) => (
                  <div key={p.id} title={p.label} data-jrni-component="field-wrap" style={{ display: "flex", alignItems: "center", gap: 6, height: 30, padding: "0 8px", border: `1px solid ${T.n4}`, borderRadius: 4, background: T.white }}>
                    <span style={{ color: T.n2, flexShrink: 0, display: "inline-flex" }}><SocialGlyph id={p.id} size={13}/></span>
                    <input value={sp.socials?.[p.id] || ""} placeholder={p.placeholder} onChange={(e) => patchSocial(i, p.id, e.target.value)}
                      style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 12, fontFamily: T.font, color: T.n1, minWidth: 0 }}/>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
        {/* ui-raw: dashed "add new row" placeholder affordance — same
            intentional non-Btn treatment as ListEditor's add button. */}
        <button onClick={() => onChange([...list, { name: "", logo: "", bio: "", socials: {} }])} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 10px", border: `1px dashed ${T.n4}`, borderRadius: 6, background: T.white, color: T.p1, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
          <Icon.plus size={13}/> {t("landing.builderHelpers.addSponsor", "Add sponsor")}</button>
      </div>
    </div>
  );
}

/** YouTube / Vimeo / direct-file resolver for the video block. */
export function resolveVideo(url: unknown): { embed?: string; file?: string } {
  const u = String(url || "").trim();
  if (!u) return {};
  const yt = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  if (yt) return { embed: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { embed: `https://player.vimeo.com/video/${vm[1]}` };
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(u)) return { file: u };
  return {};
}

/** A bare IANA zone id from a stored zone value (CC 0256). The event config keeps the picker's
 *  decorated label, "(GMT-5) America/New_York"; the id is its last word, as CreateEventDates
 *  reads it. Anything that isn't a zone Intl accepts gives "" (never the visitor's zone). */
export function bareZone(v?: unknown): string {
  const id = String(v ?? "").trim().split(/\s+/).pop() || "";
  if (!id) return "";
  try { new Intl.DateTimeFormat("en-US", { timeZone: id }); return id; } catch { return ""; }
}

/** The countdown's target as one absolute moment, the same for every viewer (operator,
 *  CC 0251). A value with its own offset ("…+01:00", "…Z") is already absolute. A bare
 *  wall clock ("2026-10-20T11:00", what the inspector stores) is read in `zone`, the
 *  event's own zone, never the viewer's browser zone. */
export function countdownInstant(target?: string, zone?: string): number {
  const v = String(target || "").trim();
  if (!v) return NaN;
  if (/(Z|[+-]\d{2}:?\d{2})$/i.test(v)) return new Date(v).getTime();
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(v);
  if (!m) return new Date(v).getTime();
  /* No zone we can read means no countdown: guessing the visitor's zone is the bug (CC 0256). */
  const z = bareZone(zone);
  if (!z) return NaN;
  const iso = wallClockToIso(m[1], m[2], z);
  return iso ? new Date(iso).getTime() : NaN;
}

/** "11:00 CT · 17:00 your time": the start in the event's zone, and in the viewer's own
 *  zone when it differs; once when they agree. Abbreviation and clock from the page locale. */
function countdownWhenLine(at: number, zoneIn?: string): string {
  if (!Number.isFinite(at)) return "";
  const zone = bareZone(zoneIn) || undefined;
  const loc = currentLocale() || undefined;
  const fmt = (tz?: string) => { try { return new Intl.DateTimeFormat(loc, { hour: "numeric", minute: "2-digit", ...(tz ? { timeZone: tz } : {}) }).format(new Date(at)); } catch { return ""; } };
  const abbr = (tz?: string) => { try { return new Intl.DateTimeFormat(loc, { timeZoneName: "short", ...(tz ? { timeZone: tz } : {}) }).formatToParts(new Date(at)).find((p) => p.type === "timeZoneName")?.value || tzNameLabel(tz, at); } catch { return tzNameLabel(tz, at); } };
  let viewer = "";
  try { viewer = new Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { /* unknown viewer zone */ }
  const offset = (tz?: string) => { try { const p = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longOffset" }).formatToParts(new Date(at)).find((x) => x.type === "timeZoneName")?.value; return p || ""; } catch { return ""; } };
  const eventPart = `${fmt(zone)} ${abbr(zone)}`.trim();
  if (!zone || !viewer || viewer === zone || offset(viewer) === offset(zone)) return eventPart;
  return `${eventPart} · ${fmt(viewer)} your time`;
}

function CountdownBlock({ title, target, zone, theme }: { title?: string; target?: string; zone?: string; theme: LandingTheme }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const end = countdownInstant(target, zone);
  const when = countdownWhenLine(end, zone);
  const valid = Number.isFinite(end);
  const diff = valid ? Math.max(0, end - now) : 0;
  const s = Math.floor(diff / 1000);
  const parts = [
    { v: Math.floor(s / 86400), l: "days" },
    { v: Math.floor((s % 86400) / 3600), l: "hrs" },
    { v: Math.floor((s % 3600) / 60), l: "min" },
    { v: s % 60, l: "sec" },
  ];
  return (
    <div style={{ textAlign: "center" }}>
      {title ? <div style={{ fontSize: 13, fontWeight: 600, color: T.soft, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>{title}</div> : null}
      {valid ? (
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          {parts.map((p) => (
            <div key={p.l} style={{ minWidth: 72, padding: "12px 8px", borderRadius: 10, background: alpha(theme.brand, "12"), border: `1px solid ${alpha(theme.brand, "33")}` }}>
              <div style={{ fontSize: 30, fontWeight: 800, color: theme.brand, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{String(p.v).padStart(2, "0")}</div>
              <div style={{ fontSize: 11, color: T.soft, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 4 }}>{p.l}</div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: T.soft }}>{target && !/(Z|[+-]\d{2}:?\d{2})$/i.test(String(target).trim()) && !bareZone(zone)
          ? "Set the event's time zone to show the countdown."
          : i18nT("landing.builderHelpers.setATargetDateAmpTimeInTheInspector.rendered", "Set a target date & time in the inspector.")}</div>
      )}
      {valid && when ? (
        <div data-testid="countdown-when" style={{ fontSize: 13, color: T.soft, marginTop: 12, fontVariantNumeric: "tabular-nums" }}>{when}</div>
      ) : null}
    </div>
  );
}

// ── Block inspector ─────────────────────────────────────────────────────
export function BlockInspector({ type, cfg, onChange, allBlocks = [], staffMembers = [], staffGroups = [] }: { type: string; cfg: Record<string, unknown>; onChange: (p: Record<string, unknown>) => void; allBlocks?: LandingBlock[]; staffMembers?: { id: string; name: string; email?: string }[]; staffGroups?: { id: string; name: string }[] }) {
  const t = useT();
  if (type === "row") {
    const cells = getRowCells(cfg);
    const cols = cells.length;
    const setCols = (n: number) => onChange({ ratio: n === 3 ? "1-1-1" : "1-1", cells: repackCells(cells, n) });
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <NativeSelect label={t("landing.builderHelpers.columns", "Columns")} value={String(cols)} options={[["2", "Two columns"], ["3", "Three columns"]]} onChange={(v) => setCols(parseInt(v, 10))}/>
        {cols === 2 ? (
          <NativeSelect label={t("landing.builderHelpers.widthSplit", "Width split")} value={String(cfg.ratio || "1-1")} options={[["1-1", "Even · 50 / 50"], ["2-1", "Wide left · 66 / 33"], ["1-2", "Wide right · 33 / 66"]]} onChange={(v) => onChange({ ratio: v })}/>
        ) : null}
        <NativeSelect label={t("landing.builderHelpers.gapBetweenColumns", "Gap between columns")} value={String(cfg.gap ?? 20)} options={[["12", "Tight"], ["20", "Standard"], ["32", "Roomy"]]} onChange={(v) => onChange({ gap: parseInt(v, 10) })}/>
        <ToggleRow label={t("landing.builderHelpers.stackOnMobile", "Stack on mobile")} on={cfg.stackMobile !== false} onChange={(v) => onChange({ stackMobile: v })}/>
        <hr style={{ border: "none", borderTop: `1px solid ${T.border}`, margin: "2px 0" }}/>
        <NativeSelect label={t("landing.builderHelpers.sectionBackground", "Section background")} value={String(cfg.bandKind || "none")} options={[["none", "None"], ["color", "Color"], ["image", "Image"]]} onChange={(v) => onChange({ bandKind: v })}/>
        {cfg.bandKind === "color" ? <ColorInput label={t("landing.builderHelpers.backgroundColor", "Background color")} value={cfg.bandBg} onChange={(v) => onChange({ bandBg: v })}/> : null}
        {cfg.bandKind === "image" ? <LandingHeroImage cfg={{ imageUrl: cfg.bandImage }} onChange={(p) => onChange({ bandImage: (p as { imageUrl?: unknown }).imageUrl })}/> : null}
        {cfg.bandKind === "image" ? <LabeledSlider label={t("landing.builderHelpers.imageOpacity", "Image opacity")} value={cfg.bandOpacity} onChange={(v) => onChange({ bandOpacity: v })} format={(n) => `${Math.round(n * 100)}%`}/> : null}
        <NativeSelect label={t("landing.builderHelpers.verticalPadding", "Vertical padding")} value={String(cfg.pad ?? 20)} options={[["12", "Tight"], ["20", "Standard"], ["40", "Roomy"], ["64", "Spacious"]]} onChange={(v) => onChange({ pad: parseInt(v, 10) })}/>
        <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.5, padding: "8px 10px", background: T.n6, borderRadius: 6 }}>
          {t("landing.builderHelpers.dragAnyBlockFromTheLeftIntoAColumnOrDragExisting", "Drag any block from the left into a column, or drag existing blocks between columns. Columns hold everything except the hero and other column rows.")}</div>
      </div>
    );
  }
  if (type === "hero") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <ColorWheelField label={t("landing.builderHelpers.eventNameColor", "Event name color")} value={cfg.headlineColor} defaultValue="#FFFFFF" hint={t("landing.builderHelpers.hint3", "Colors the event title shown in the hero.")} onChange={(v) => onChange({ headlineColor: v })}/>
      <ColorWheelField label={t("landing.builderHelpers.descriptionColor", "Description color")} value={cfg.subheadColor} defaultValue="#FFFFFF" onChange={(v) => onChange({ subheadColor: v })}/>
      <NativeSelect label={t("landing.builderHelpers.background", "Background")} value={cfg.bgKind} options={[["gradient","Gradient"],["solid","Solid color"],["image","Image"]]} onChange={(v) => onChange({ bgKind: v })}/>
      {cfg.bgKind === "gradient" ? <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}><ColorInput label={t("landing.builderHelpers.from", "From")} value={cfg.from} onChange={(v) => onChange({ from: v })}/><ColorInput label={t("landing.builderHelpers.to", "To")} value={cfg.to} onChange={(v) => onChange({ to: v })}/></div> : null}
      {cfg.bgKind === "solid" ? <ColorInput label={t("landing.builderHelpers.color", "Color")} value={cfg.solid} onChange={(v) => onChange({ solid: v })}/> : null}
      {cfg.bgKind === "image" ? <LandingHeroImage cfg={cfg} onChange={onChange}/> : null}
      {cfg.bgKind === "image" ? <LabeledSlider label={t("landing.builderHelpers.imageOpacity", "Image opacity")} value={cfg.imageOpacity} onChange={(v) => onChange({ imageOpacity: v })} format={(n) => `${Math.round(n * 100)}%`}/> : null}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <NativeSelect label={t("landing.builderHelpers.padding", "Padding")} value={String(cfg.padding)} options={[["24","24px"],["32","32px"],["40","40px"],["56","56px"]]} onChange={(v) => onChange({ padding: parseInt(v, 10) })}/>
        <NativeSelect label={t("landing.builderHelpers.align", "Align")}   value={String(cfg.align)}   options={[["left","Left"],["center","Center"]]} onChange={(v) => onChange({ align: v })}/>
      </div>
      <ToggleRow label={t("landing.builderHelpers.showCta", "Show CTA")} on={cfg.showCTA} onChange={(v) => onChange({ showCTA: v })}/>
      {cfg.showCTA ? <LabeledInput label={t("landing.builderHelpers.ctaLabel", "CTA label")} value={cfg.ctaLabel} onChange={(v) => onChange({ ctaLabel: v })}/> : null}
    </div>
  );
  if (type === "dates") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.date", "Date")} value={cfg.date} onChange={(v) => onChange({ date: v })}/>
      <LabeledInput label={t("landing.builderHelpers.time", "Time")} value={cfg.time} onChange={(v) => onChange({ time: v })}/>
      <LabeledInput label={t("landing.builderHelpers.location", "Location")} value={cfg.loc} onChange={(v) => onChange({ loc: v })}/>
      <NativeSelect label={t("landing.builderHelpers.style", "Style")} value={cfg.style} options={[["card","Card"],["inline","Inline"]]} onChange={(v) => onChange({ style: v })}/>
    </div>
  );
  if (type === "speakers") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.sectionTitle", "Section title")} value={cfg.title} onChange={(v) => onChange({ title: v })}/>
      <NativeSelect label={t("landing.builderHelpers.layout", "Layout")} value={cfg.layout} options={[["grid","Grid"],["list","List"]]} onChange={(v) => onChange({ layout: v })}/>
      <SpeakerListEditor items={resolveSpeakerItems(cfg)} onChange={(items) => onChange({ items })}/>
    </div>
  );
  if (type === "tickets") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.sectionTitle", "Section title")} value={cfg.title} onChange={(v) => onChange({ title: v })}/>
      <NativeSelect label={t("landing.builderHelpers.layout", "Layout")} value={cfg.layout} options={[["list","Stacked list"],["grid","Grid"]]} onChange={(v) => onChange({ layout: v })}/>
      <ToggleRow label={t("landing.builderHelpers.showSoldCount", "Show sold count")} on={cfg.showSold} onChange={(v) => onChange({ showSold: v })}/>
    </div>
  );
  if (type === "agenda") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.sectionTitle", "Section title")} value={cfg.title} onChange={(v) => onChange({ title: v })}/>
      <NativeSelect label={t("landing.builderHelpers.style", "Style")} value={cfg.style} options={[["timeline","Timeline"],["table","Table"]]} onChange={(v) => onChange({ style: v })}/>
      <ListEditor label={t("landing.builderHelpers.agendaItems", "Agenda items")} items={resolveAgendaItems(cfg) as Record<string, string>[]}
        columns={[{ key: "time", placeholder: t("landing.builderHelpers.text1000Am", "10:00 AM"), width: 120 }, { key: "title", placeholder: t("landing.builderHelpers.whatSHappening", "What's happening") }]}
        blank={{ time: "", title: "" }} addLabel="Add agenda item" onChange={(items) => onChange({ items })}/>
    </div>
  );
  if (type === "map") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.sectionTitle", "Section title")} value={cfg.title} onChange={(v) => onChange({ title: v })}/>
      <LabeledInput label={t("landing.builderHelpers.address", "Address")} value={cfg.address} onChange={(v) => onChange({ address: v })}/>
      <NativeSelect label={t("landing.builderHelpers.height", "Height")} value={String(cfg.height)} options={[["180","Compact"],["240","Standard"],["360","Tall"]]} onChange={(v) => onChange({ height: parseInt(v, 10) })}/>
    </div>
  );
  if (type === "faq") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.sectionTitle", "Section title")} value={cfg.title} onChange={(v) => onChange({ title: v })}/>
      <ListEditor label={t("landing.builderHelpers.questionsAnswers", "Questions & answers")} items={resolveFaqItems(cfg) as Record<string, string>[]}
        columns={[{ key: "q", placeholder: t("landing.builderHelpers.question", "Question") }, { key: "a", placeholder: t("landing.builderHelpers.answer", "Answer"), type: "textarea" }]}
        blank={{ q: "", a: "" }} addLabel="Add question" onChange={(items) => onChange({ items })}/>
    </div>
  );
  if (type === "signup") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.sectionTitle", "Section title")} value={cfg.title} onChange={(v) => onChange({ title: v })}/>
      <LabeledInput label={t("landing.builderHelpers.ctaButton", "CTA button")} value={cfg.cta} onChange={(v) => onChange({ cta: v })}/>
      <ToggleRow label={t("landing.builderHelpers.showNameField", "Show name field")} on={cfg.showName} onChange={(v) => onChange({ showName: v })}/>
      <ToggleRow label={t("landing.builderHelpers.showCompanyField", "Show company field")} on={cfg.showCo} onChange={(v) => onChange({ showCo: v })}/>
    </div>
  );
  if (type === "text") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.headingOptional", "Heading (optional)")} value={cfg.heading} onChange={(v) => onChange({ heading: v })}/>
      <LabeledTextarea label={t("landing.builderHelpers.body", "Body")} value={cfg.body} onChange={(v) => onChange({ body: v })}/>
      <NativeSelect label={t("landing.builderHelpers.align", "Align")} value={cfg.align} options={[["left", "Left"], ["center", "Center"]]} onChange={(v) => onChange({ align: v })}/>
    </div>
  );
  if (type === "image") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <MiniImageDrop url={cfg.imageUrl} onChange={(v) => onChange({ imageUrl: v })} hint={t("landing.builderHelpers.hint4", "PNG or JPG")}/>
      <LabeledInput label={t("landing.builderHelpers.captionOptional", "Caption (optional)")} value={cfg.caption} onChange={(v) => onChange({ caption: v })}/>
      <NativeSelect label={t("landing.builderHelpers.width", "Width")} value={String(cfg.maxW ?? 0)} options={[["0", "Full width"], ["720", "Medium"], ["480", "Small"]]} onChange={(v) => onChange({ maxW: parseInt(v, 10) })}/>
      <ToggleRow label={t("landing.builderHelpers.roundedCorners", "Rounded corners")} on={cfg.rounded} onChange={(v) => onChange({ rounded: v })}/>
    </div>
  );
  if (type === "events") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.sectionTitle", "Section title")} value={cfg.title} onChange={(v) => onChange({ title: v })}/>
      <NativeSelect label={t("landing.builderHelpers.columns", "Columns")} value={String(cfg.cols ?? 2)} options={[["1", "One"], ["2", "Two"], ["3", "Three"]]} onChange={(v) => onChange({ cols: parseInt(v, 10) })}/>
      <ToggleRow label={t("landing.builderHelpers.showCategoryFilters", "Show category filters")} on={cfg.showFilters !== false} onChange={(v) => onChange({ showFilters: v })}/>
      <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.5, padding: "8px 10px", background: T.n6, borderRadius: 6 }}>{t("landing.builderHelpers.showsTheEventsAddedToThisHubManagedInTheEventsRail", "Shows the events added to this hub (managed in the Events rail). This block lays them out on the page.")}</div>
    </div>
  );
  if (type === "gallery") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.sectionTitleOptional", "Section title (optional)")} value={cfg.title} onChange={(v) => onChange({ title: v })}/>
      <NativeSelect label={t("landing.builderHelpers.columns", "Columns")} value={String(cfg.cols ?? 3)} options={[["2", "Two"], ["3", "Three"], ["4", "Four"]]} onChange={(v) => onChange({ cols: parseInt(v, 10) })}/>
      <NativeSelect label={t("landing.builderHelpers.motionLivePage", "Motion (live page)")} value={String(cfg.motion || "grid")} options={[["grid", "Static grid"], ["buttons", "Scroll buttons"], ["auto", "Auto-scroll"]]} onChange={(v) => onChange({ motion: v })}/>
      <GalleryEditor images={(cfg.images as string[]) || []} onChange={(images) => onChange({ images })}/>
    </div>
  );
  if (type === "video") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.videoUrl", "Video URL")} value={cfg.url} onChange={(v) => onChange({ url: v })}/>
      <div style={{ fontSize: 11, color: T.soft, marginTop: -4 }}>{t("landing.builderHelpers.youtubeVimeoOrADirectMp4WebmLink", "YouTube, Vimeo, or a direct .mp4 / .webm link.")}</div>
      <LabeledInput label={t("landing.builderHelpers.captionOptional", "Caption (optional)")} value={cfg.caption} onChange={(v) => onChange({ caption: v })}/>
    </div>
  );
  if (type === "quote") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledTextarea label={t("landing.builderHelpers.quote", "Quote")} value={cfg.quote} onChange={(v) => onChange({ quote: v })}/>
      <LabeledInput label={t("landing.builderHelpers.name", "Name")} value={cfg.name} onChange={(v) => onChange({ name: v })}/>
      <LabeledInput label={t("landing.builderHelpers.roleCompany", "Role · company")} value={cfg.role} onChange={(v) => onChange({ role: v })}/>
      <MiniImageDrop url={cfg.photo} onChange={(v) => onChange({ photo: v })} height={72} hint={t("landing.builderHelpers.hint5", "Portrait (optional)")}/>
    </div>
  );
  if (type === "countdown") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.label", "Label")} value={cfg.title} onChange={(v) => onChange({ title: v })}/>
      <div>
        <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 4 }}>{t("landing.builderHelpers.countsDownTo", "Counts down to")}</label>
        <input type="datetime-local" value={String(cfg.target || "")} onChange={(e) => onChange({ target: e.target.value })}
          style={{ width: "100%", height: 36, padding: "0 10px", border: `1px solid ${T.n4}`, borderRadius: 4, background: T.white, color: T.n1, fontSize: 13, fontFamily: T.font, boxSizing: "border-box" }}/>
        <div style={{ fontSize: 11, color: T.soft, marginTop: 4 }}>{t("landing.builderHelpers.defaultsToTheEventStartWhenTheEventHasOne", "Defaults to the event start when the event has one.")}</div>
        <div style={{ fontSize: 11, color: T.soft, marginTop: 4 }}>{bareZone(cfg.targetZone) ? `In ${bareZone(cfg.targetZone)}. Every visitor counts down to the same moment.` : "In the event's time zone. Every visitor counts down to the same moment."}</div>
      </div>
    </div>
  );
  if (type === "cta") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.headline", "Headline")} value={cfg.headline} onChange={(v) => onChange({ headline: v })}/>
      <LabeledInput label={t("landing.builderHelpers.subtextOptional", "Subtext (optional)")} value={cfg.sub} onChange={(v) => onChange({ sub: v })}/>
      <LabeledInput label={t("landing.builderHelpers.buttonLabel", "Button label")} value={cfg.cta} onChange={(v) => onChange({ cta: v })}/>
      <LabeledInput label={t("landing.builderHelpers.buttonUrlOptional", "Button URL (optional)")} value={cfg.href} onChange={(v) => onChange({ href: v })}/>
      <div style={{ fontSize: 11, color: T.soft, marginTop: -4 }}>{t("landing.builderHelpers.leaveBlankToScrollGuestsToTheSignUpForm", "Leave blank to scroll guests to the sign-up form.")}</div>
      <NativeSelect label={t("landing.builderHelpers.align", "Align")} value={cfg.align} options={[["center", "Center"], ["left", "Left"]]} onChange={(v) => onChange({ align: v })}/>
      <ColorInput label={t("landing.builderHelpers.backgroundBlankThemeColor", "Background (blank = theme color)")} value={cfg.bg} onChange={(v) => onChange({ bg: v })}/>
    </div>
  );
  if (type === "sponsors") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.sectionTitle", "Section title")} value={cfg.title} onChange={(v) => onChange({ title: v })}/>
      <NativeSelect label={t("landing.builderHelpers.columns", "Columns")} value={String(cfg.cols ?? 4)} options={[["2", "Two"], ["3", "Three"], ["4", "Four"], ["5", "Five"]]} onChange={(v) => onChange({ cols: parseInt(v, 10) })}/>
      <NativeSelect label={t("landing.builderHelpers.motionLivePage", "Motion (live page)")} value={String(cfg.motion || "grid")} options={[["grid", "Static grid"], ["buttons", "Scroll buttons"], ["auto", "Auto-scroll"]]} onChange={(v) => onChange({ motion: v })}/>
      <ToggleRow label={t("landing.builderHelpers.grayscaleLogos", "Grayscale logos")} on={cfg.grayscale} onChange={(v) => onChange({ grayscale: v })}/>
      <SponsorsEditor items={(cfg.items as Sponsor[]) || []} onChange={(items) => onChange({ items })}/>
    </div>
  );
  if (type === "stats") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <ListEditor label={t("landing.builderHelpers.statTiles", "Stat tiles")} items={(cfg.items as Record<string, string>[]) || []}
        columns={[{ key: "value", placeholder: "500+" }, { key: "label", placeholder: t("landing.builderHelpers.attendees", "Attendees") }]}
        blank={{ value: "", label: "" }} addLabel="Add stat" onChange={(items) => onChange({ items })}/>
    </div>
  );
  if (type === "social") {
    const smode = String(cfg.smode || "follow");
    const links = (cfg.links as Record<string, string>) || {};
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <LabeledInput label={t("landing.builderHelpers.sectionTitle", "Section title")} value={cfg.title} onChange={(v) => onChange({ title: v })}/>
        <NativeSelect label={t("landing.builderHelpers.mode", "Mode")} value={smode} options={[["follow", "Follow links"], ["share", "Share this page"]]} onChange={(v) => onChange({ smode: v })}/>
        {smode === "follow" ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {SOCIAL_PLATFORMS.map((p) => (
              <div key={p.id} title={p.label} data-jrni-component="field-wrap" style={{ display: "flex", alignItems: "center", gap: 6, height: 30, padding: "0 8px", border: `1px solid ${T.n4}`, borderRadius: 4, background: T.white }}>
                <span style={{ color: T.n2, flexShrink: 0, display: "inline-flex" }}><SocialGlyph id={p.id} size={13}/></span>
                <input value={links[p.id] || ""} placeholder={p.placeholder} onChange={(e) => onChange({ links: { ...links, [p.id]: e.target.value } })}
                  style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 12, fontFamily: T.font, color: T.n1, minWidth: 0 }}/>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.5, padding: "8px 10px", background: T.n6, borderRadius: 6 }}>{t("landing.builderHelpers.guestsShareTheLivePageToXLinkedinFacebookOrCopyThe", "Guests share the live page to X, LinkedIn, Facebook, or copy the link.")}</div>
        )}
      </div>
    );
  }
  if (type === "nav") {
    const items = (Array.isArray(cfg.items) ? cfg.items : []) as { uid: string; label: string }[];
    const candidates = allBlocks.filter((b) => b.type !== "nav");
    const secLabel = (b: LandingBlock) => String((b.cfg as Record<string, unknown>).title || (b.cfg as Record<string, unknown>).headline || (b.cfg as Record<string, unknown>).heading || (LANDING_BLOCKS.find((d) => d.id === b.type)?.l) || b.type);
    const isOn = (uid: string) => items.some((x) => x.uid === uid);
    const toggle = (b: LandingBlock) => isOn(b.uid) ? onChange({ items: items.filter((x) => x.uid !== b.uid) }) : onChange({ items: [...items, { uid: b.uid, label: secLabel(b) }] });
    const setLabel = (uid: string, label: string) => onChange({ items: items.map((x) => x.uid === uid ? { ...x, label } : x) });
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <NativeSelect label={t("landing.builderHelpers.align", "Align")} value={String(cfg.align || "left")} options={[["left", "Left"], ["center", "Center"], ["right", "Right"]]} onChange={(v) => onChange({ align: v })}/>
        <ToggleRow label={t("landing.builderHelpers.stickToTopOnScroll", "Stick to top on scroll")} on={cfg.sticky !== false} onChange={(v) => onChange({ sticky: v })}/>
        <div>
          <label style={{ fontSize: 13, color: T.n1, fontWeight: 500, display: "block", marginBottom: 6 }}>{t("landing.builderHelpers.linkToSections", "Link to sections")}</label>
          {candidates.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {candidates.map((b) => {
                const on = isOn(b.uid);
                const it = items.find((x) => x.uid === b.uid);
                return (
                  <div key={b.uid} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Checkbox checked={on} onChange={() => toggle(b)} aria-label={on ? t("common.actions.remove", "Remove") : t("common.actions.add", "Add")}/>
                    <Input value={it ? it.label : secLabel(b)} disabled={!on} onChange={(e) => setLabel(b.uid, e.target.value)} size="sm" style={{ flex: 1 }}/>
                  </div>
                );
              })}
            </div>
          ) : <div style={{ fontSize: 12, color: T.soft }}>{t("landing.builderHelpers.addOtherBlocksFirstThenLinkThemHere", "Add other blocks first, then link them here.")}</div>}
        </div>
      </div>
    );
  }
  if (type === "chat") {
    const kind = String(cfg.recipientKind || "staff");
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <LabeledInput label={t("landing.builderHelpers.title", "Title")} value={cfg.title} onChange={(v) => onChange({ title: v })}/>
        <LabeledTextarea label={t("landing.builderHelpers.greetingMessage", "Greeting message")} value={cfg.greeting} onChange={(v) => onChange({ greeting: v })}/>
        <NativeSelect label={t("landing.builderHelpers.chatRequestsGoTo", "Chat requests go to")} value={kind} options={[["staff", "A staff member"], ["group", "A staff group"], ["ai", "AI assistant"]]} onChange={(v) => onChange({ recipientKind: v, recipientId: "", recipientLabel: "", recipientEmail: "" })}/>
        {kind === "staff" ? (
          <NativeSelect label={t("landing.builderHelpers.staffMember", "Staff member")} value={String(cfg.recipientId || "")} options={[["", "Select…"], ...staffMembers.map((m) => [m.id, m.name] as [string, string])]} onChange={(id) => { const m = staffMembers.find((x) => x.id === id); onChange({ recipientId: id, recipientLabel: m?.name || "", recipientEmail: m?.email || "" }); }}/>
        ) : null}
        {kind === "group" ? (
          <NativeSelect label={t("landing.builderHelpers.staffGroup", "Staff group")} value={String(cfg.recipientId || "")} options={[["", "Select…"], ...staffGroups.map((g) => [g.id, g.name] as [string, string])]} onChange={(id) => { const g = staffGroups.find((x) => x.id === id); onChange({ recipientId: id, recipientLabel: g?.name || "" }); }}/>
        ) : null}
        {kind !== "ai" ? <LabeledInput label={kind === "group" ? t("landing.landingBuilderHelpers.groupInboxEmail", "Group inbox email") : t("landing.landingBuilderHelpers.fallbackEmail", "Fallback email")} value={cfg.fallbackEmail} onChange={(v) => onChange({ fallbackEmail: v })}/> : null}
        <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.5 }}>{kind === "ai" ? t("landing.builderHelpers.text4", "AI chat connects once live chat is enabled for your workspace.") : t("landing.builderHelpers.text5", "Guests' messages open their email to the selected recipient (staff email, or the fallback).")}</div>
      </div>
    );
  }
  if (type === "contact") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <LabeledInput label={t("landing.builderHelpers.title", "Title")} value={cfg.title} onChange={(v) => onChange({ title: v })}/>
      <LabeledTextarea label={t("landing.builderHelpers.introText", "Intro text")} value={cfg.blurb} onChange={(v) => onChange({ blurb: v })}/>
      <LabeledInput label={t("landing.builderHelpers.contactEmail", "Contact email")} value={cfg.email} onChange={(v) => onChange({ email: v })}/>
      <LabeledInput label={t("landing.builderHelpers.phoneOptional", "Phone (optional)")} value={cfg.phone} onChange={(v) => onChange({ phone: v })}/>
      <ToggleRow label={t("landing.builderHelpers.showMessageForm", "Show message form")} on={cfg.showForm} onChange={(v) => onChange({ showForm: v })}/>
    </div>
  );
  return null;
}

// ── RenderedBlock ───────────────────────────────────────────────────────
/** "live": real inputs + awaited submitPublicEventRegistration (the same op the
 *  attend.* form submits) with honest error/confirmation states. "draft":
 *  disabled fields + notice (publishState 'draft-preview'; the register op
 *  409s event_not_published anyway). "builder": the inert design placeholders. */
type RegTier = NonNullable<PublicEventDetailLike["ticketTiers"]>[number];

/** Format a tier price for the registration summary (mirrors the tickets block). */
function regTierPrice(t?: RegTier | null): string {
  if (!t) return "";
  if (t.soldOut) return "Sold out";
  if (typeof t.price === "string" && t.price) return t.price;
  if (typeof t.price === "number") return t.price === 0 ? "Free" : `$${t.price}`;
  if (typeof t.priceMinor === "number") return t.priceMinor === 0 ? "Free" : new Intl.NumberFormat(undefined, { style: "currency", currency: t.currency || "USD" }).format(t.priceMinor / 100);
  return "Free";
}

// ── Req 4/5 helpers — payment detection + capture-form field model ──────
/** True when a tier takes payment. Prefers the contract's `requiresPayment`
 *  flag (2026-07-14 docket — additive, data-layer); until it ships, falls back
 *  to the tier's kind/price so paid tiers behave correctly against today's
 *  canonical payloads. Never baked per-tier data — pure derivation. */
export function tierRequiresPayment(t?: RegTier | null): boolean {
  if (!t || t.soldOut) return false;
  if (typeof t.requiresPayment === "boolean") return t.requiresPayment;
  if (t.kind === "paid") return true;
  if (typeof t.priceMinor === "number") return t.priceMinor > 0;
  if (typeof t.price === "number") return t.price > 0;
  if (typeof t.price === "string") return /[1-9]/.test(t.price.replace(/[^0-9.]/g, ""));
  return false;
}

/** Mirror of the publish step's #816 stdFields→registrationQuestions mapping
 *  (CreateEventPublish.tsx) so the wizard's builder preview renders the SAME
 *  capture form the publish payload declares. Keep the two in lockstep. */
export function stdFieldsToRegistrationQuestions(modesRaw: unknown): RegistrationQuestionLike[] {
  const modes = (modesRaw && typeof modesRaw === "object" ? modesRaw : {}) as Record<string, string>;
  const mode = (k: string, dflt: string) => modes[k] || dflt;
  const qs: RegistrationQuestionLike[] = [
    { id: "default-firstName", fieldKey: "firstName", label: i18nT("landing.builderHelpers.firstName", "First name"), type: "short", required: true },
    { id: "default-lastName", fieldKey: "lastName", label: i18nT("landing.builderHelpers.lastName", "Last name"), type: "short", required: true },
    { id: "default-email", fieldKey: "email", label: i18nT("landing.builderHelpers.emailAddress", "Email address"), type: "email", validation: "email", required: true },
    { id: "default-phone", fieldKey: "phone", label: i18nT("landing.builderHelpers.phoneNumber", "Phone number"), type: "phone", validation: "phone-e164", required: mode("phone", "required") === "required" },
  ];
  ([["homeAddress", "Home address", "long"], ["company", "Company / organisation", "short"], ["title", "Job title", "short"]] as const).forEach(([id, lbl, type]) => {
    const m = mode(id, "optional");
    if (m === "off") return;
    qs.push({ id: `std-${id}`, label: lbl, type, required: m === "required" });
  });
  return qs;
}

interface RegField { key: string; fieldKey?: string; label: string; type: string; required: boolean; validation?: string; help?: string; choices?: string[] }

/** The capture form is a DEFAULT widget (Req 5): the EVENT's configured
 *  registrationQuestions drive it verbatim — required flags included; with none
 *  configured, a minimal capture set renders (ui-config structure, not data). */
function resolveRegistrationFields(event: PublicEventDetailLike | null | undefined, c: Record<string, unknown>): RegField[] {
  const qs = event?.registrationQuestions;
  if (Array.isArray(qs) && qs.length) {
    return qs.map((q, i) => ({
      key: String(q.fieldKey || q.id || `q${i}`),
      fieldKey: q.fieldKey,
      label: String(q.label || q.fieldKey || `Question ${i + 1}`),
      type: String(q.type || "short"),
      // WYSIWYG (operator, 2026-07-14): the event object's flag is the truth —
      // never forced or "locked" here.
      required: q.required === true,
      validation: q.validation,
      help: q.help,
      choices: Array.isArray(q.choices) && q.choices.length ? q.choices : undefined,
    }));
  }
  // No configured questions on the event ⇒ the minimal capture set. Only email
  // is marked required (the register op itself rejects a missing/invalid
  // email); names carry no fabricated compulsion.
  const fields: RegField[] = [];
  if (c.showName !== false) {
    fields.push({ key: "firstName", fieldKey: "firstName", label: i18nT("landing.builderHelpers.firstName", "First name"), type: "short", required: false });
    fields.push({ key: "lastName", fieldKey: "lastName", label: i18nT("landing.builderHelpers.lastName", "Last name"), type: "short", required: false });
  }
  fields.push({ key: "email", fieldKey: "email", label: i18nT("landing.builderHelpers.emailAddress", "Email address"), type: "email", validation: "email", required: true });
  if (c.showCo) fields.push({ key: "company", label: i18nT("landing.builderHelpers.companyOrganisation", "Company / organisation"), type: "short", required: false });
  return fields;
}

function regFieldValid(f: RegField, raw: string): boolean {
  const v = raw.trim();
  if (!v) return !f.required;
  if (f.validation === "email" || f.type === "email") return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
  if (f.validation === "phone-e164" || f.type === "phone") return /^\+?[0-9][0-9\s().-]{5,18}$/.test(v);
  if (f.type === "number") return /^-?\d+([.,]\d+)?$/.test(v);
  return true;
}

/* PDD-084 (2026-09-01): PAYMENT_UNAVAILABLE means the capture SUCCEEDED and the
 * confirmation webhook has not landed yet. The guest's money may already be
 * gone, so the only correct response is to wait and ask again — never an error,
 * never a re-prompt for a card. Bounded so a stuck webhook eventually surfaces
 * the "we're still waiting" copy instead of spinning forever; the caller stays
 * busy throughout, so the guest sees one continuous "Processing…" rather than a
 * flicker of failure. Any other error, including the other two payment codes,
 * rethrows on the first attempt — retrying a CONFLICT just re-sends the same
 * mismatch. */
const PAYMENT_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000];
async function withPaymentRetry<T>(attempt: () => Promise<T>): Promise<T> {
  for (let i = 0; ; i++) {
    try { return await attempt(); } catch (e) {
      const code = String((e as { code?: string }).code ?? "");
      if (code !== "PAYMENT_UNAVAILABLE" || i >= PAYMENT_RETRY_DELAYS_MS.length) throw e;
      await new Promise((r) => setTimeout(r, PAYMENT_RETRY_DELAYS_MS[i]));
    }
  }
}

// ui-raw: SignupBlock renders the LIVE public registration + card-capture form
// for a published event. Every field/button here is styled by `theme.brand`
// (the per-tenant/per-event brand color resolved at runtime) — jrni-ui's
// Input/Textarea/Btn hardcode fixed T.p1/T.brandFill tokens and have no color
// override seam, so swapping them would strip the per-event branding this
// screen exists to render. Left raw for parity; not touched below.
function SignupBlock({ c, theme, mode, eventId, event, selectedTier, multiTier }: { c: Record<string, unknown>; theme: LandingTheme; mode: LandingRenderMode; eventId?: string; event?: PublicEventDetailLike | null; selectedTier?: RegTier | null; multiTier?: boolean }) {
  const t = useT();
  const fields = resolveRegistrationFields(event, c);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [step, setStep] = useState<"form" | "payment">("form"); // ui-local — capture→payment step nav (Req 4)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  /* Already-registered (JAI-2279, absorbed 2026-10-05): a duplicate registration answers alreadyRegistered with no reference; it is a neutral terminal state, never a fresh confirmation. */
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [doneEmail, setDoneEmail] = useState("");
  const [paidLabel, setPaidLabel] = useState<string | null>(null);
  // JAI-579 — the Apple Wallet pass URL for THIS registration, taken from the
  // response's ticket link. Presence of the field is the whole gate: a tenant
  // without Wallet configured omits it (never an empty string), and the
  // confirmation panel then renders exactly as it does today. Null until the
  // register op answers; never constructed from credentialId + token here —
  // assembling a credential-bearing URL in a screen is data-layer work.
  const [passUrl, setPassUrl] = useState<string | null>(null);
  // Payment-capture placeholder fields (Req 4) — ui-local: no PSP op exists yet
  // (declared data-layer ask); the awaited mutation below is the real register
  // op. Card values never leave this component.
  const [card, setCard] = useState({ number: "", exp: "", cvc: "", zip: "" });
  const disabled = mode !== "live";
  const needsPayment = tierRequiresPayment(selectedTier);
  // REQ-045 (merged 2026-07-23): the event's public payment config picks the
  // checkout widget — Stripe → the existing card payment step, PayPal → Smart
  // Buttons. Fetched for live checkout and for the builder's DISABLED payment
  // placeholder; a provider renders only when configured + verified. Absent/
  // failed config keeps today's behavior (card step) — no invented provider state.
  const [payCfg, setPayCfg] = useState<EventPublicPaymentConfig | null>(null);
  // Defect fix 2026-07-24: a null payCfg is NOT "legacy card path" — it's an
  // unknown provider (in-flight or failed fetch). Paid checkout is BLOCKED until
  // the config genuinely loads; only status "ok" may route to a provider widget.
  const [payCfgStatus, setPayCfgStatus] = useState<"loading" | "ok" | "error">("loading");
  const [payCfgEpoch, setPayCfgEpoch] = useState(0); // bump → refetch (retry)
  useEffect(() => {
    if (!needsPayment || !eventId || (mode !== "live" && mode !== "builder")) return;
    let alive = true;
    setPayCfgStatus("loading");
    getEventPublicPaymentConfig(eventId)
      .then((cfg) => { if (alive) { setPayCfg(cfg); setPayCfgStatus("ok"); } })
      .catch((e) => { console.warn("getEventPublicPaymentConfig unavailable:", e); if (alive) setPayCfgStatus("error"); });
    return () => { alive = false; };
  }, [eventId, needsPayment, mode, payCfgEpoch]);
  const paypalVerified = payCfg?.provider === "paypal" && payCfg.verified === true && !!payCfg.paypal;
  const paypalUnready = payCfg?.provider === "paypal" && !(payCfg.verified === true && !!payCfg.paypal);
  // REQ-060: Adyen readiness needs only provider+verified — the Drop-in's own
  // params (sessionData/clientKey/environment) come from createEventAdyenSession.
  const adyenVerified = payCfg?.provider === "adyen" && payCfg.verified === true;
  const adyenUnready = payCfg?.provider === "adyen" && payCfg.verified !== true;
  const allValid = fields.every((f) => regFieldValid(f, String(vals[f.key] ?? "")));
  const ghosted = mode === "live" && !allValid;
  const setVal = (k: string) => (v: string) => setVals((s) => ({ ...s, [k]: v }));
  const touch = (k: string) => setTouched((s) => (s[k] ? s : { ...s, [k]: true }));

  const inputStyle = (bad?: boolean): React.CSSProperties => ({ height: 40, padding: "0 12px", border: `1px solid ${bad ? T.red1 : T.n4}`, borderRadius: 4, background: disabled ? T.n6 : T.white, fontSize: 13, color: disabled ? T.n3 : T.n1, fontFamily: "inherit", width: "100%", boxSizing: "border-box" });
  const inertField: React.CSSProperties = { height: 40, padding: "0 12px", display: "flex", alignItems: "center", border: `1px solid ${T.n4}`, borderRadius: 4, background: T.white, fontSize: 13, color: T.n3, boxSizing: "border-box" };
  // Req 5: a required field NEVER reads "optional" (and vice-versa).
  const labelRow = (f: RegField) => (
    <div style={{ fontSize: 13, color: T.n1, fontWeight: 500, marginBottom: 4 }}>
      {f.label}{f.required
        ? <span aria-hidden="true" style={{ color: T.red1 }}> *</span>
        : <span style={{ color: T.soft, fontWeight: 400 }}> {t("landing.builderHelpers.optional", "(optional)")}</span>}
    </div>
  );
  const fieldControl = (f: RegField) => {
    const raw = String(vals[f.key] ?? "");
    const bad = !!touched[f.key] && !regFieldValid(f, raw);
    const firstAttr = f.key === fields[0]?.key ? { "data-lp-signup-first-field": true } : {};
    if (mode === "builder") {
      if (f.type === "check") return (
        <div {...firstAttr} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
          <span style={{ width: 16, height: 16, borderRadius: 3, border: `1.5px solid ${T.n4}`, background: T.white, flexShrink: 0 }}/>
          <span style={{ fontSize: 12.5, color: T.n3 }}>{f.help || t("landing.builderHelpers.text13", "Yes")}</span>
        </div>
      );
      if (f.type === "select" || f.type === "choice") return <div {...firstAttr} style={{ ...inertField, display: "flex", alignItems: "center", justifyContent: "space-between" }}><span>{t("landing.builderHelpers.choose", "Choose…")}</span><Icon.chev size={12}/></div>;
      const ph = f.type === "email" ? "name@company.com" : f.type === "phone" ? "+1 555 000 0000" : f.type === "date" ? "Select a date" : f.type === "long" ? "Your answer" : "";
      return <div {...firstAttr} style={f.type === "long" ? { ...inertField, height: 64, alignItems: "flex-start", padding: "10px 12px" } : inertField}>{ph}</div>;
    }
    if (f.type === "long") return <textarea {...firstAttr} value={raw} disabled={disabled} rows={3} onChange={(e) => setVal(f.key)(e.target.value)} onBlur={() => touch(f.key)} aria-label={f.label} style={{ ...inputStyle(bad), height: "auto", padding: "10px 12px", resize: "vertical", lineHeight: 1.5 }}/>;
    if (f.type === "select" || f.type === "choice") return (
      <select {...firstAttr} value={raw} disabled={disabled} onChange={(e) => { setVal(f.key)(e.target.value); touch(f.key); }} aria-label={f.label} style={{ ...inputStyle(bad), height: 40, cursor: disabled ? "default" : "pointer" }}>
        <option value="">{t("landing.builderHelpers.choose", "Choose…")}</option>
        {(f.choices || []).map((ch) => <option key={ch} value={ch}>{ch}</option>)}
      </select>
    );
    if (f.type === "check") return (
      <label {...firstAttr} style={{ display: "flex", alignItems: "center", gap: 8, cursor: disabled ? "default" : "pointer" }}>
        <input type="checkbox" checked={raw === "yes"} disabled={disabled} onChange={(e) => { setVal(f.key)(e.target.checked ? "yes" : ""); touch(f.key); }} aria-label={f.label} style={{ width: 16, height: 16, accentColor: theme.brand }}/>
        <span style={{ fontSize: 12.5, color: T.n2 }}>{f.help || t("landing.builderHelpers.text13", "Yes")}</span>
      </label>
    );
    if (f.type === "file") return <input {...firstAttr} type="file" disabled={disabled} onChange={(e) => { setVal(f.key)(e.target.files?.[0]?.name || ""); touch(f.key); }} aria-label={f.label} style={{ ...inputStyle(bad), padding: "8px 12px", height: "auto" }}/>;
    const type = f.type === "email" ? "email" : f.type === "phone" ? "tel" : f.type === "date" ? "date" : "text";
    const ph = f.type === "email" ? "name@company.com" : f.type === "phone" ? "+1 555 000 0000" : undefined;
    return <input
      {...firstAttr}
      {...(f.fieldKey === "email" || f.type === "email" ? { "data-lp-signup-email": true } : {})}
      type={type} value={raw} disabled={disabled} placeholder={ph}
      inputMode={f.type === "number" ? "decimal" : undefined}
      onChange={(e) => setVal(f.key)(e.target.value)} onBlur={() => touch(f.key)}
      aria-label={f.label} aria-invalid={bad || undefined}
      style={inputStyle(bad)}/>;
  };
  const invalidMsg = (f: RegField) => {
    const raw = String(vals[f.key] ?? "");
    if (!touched[f.key] || regFieldValid(f, raw)) return null;
    const msg = !raw.trim() ? `${f.label} is required.`
      : (f.validation === "email" || f.type === "email") ? "Enter a valid email address."
      : (f.validation === "phone-e164" || f.type === "phone") ? "Enter a valid phone number."
      : "Enter a valid value.";
    return <div style={{ fontSize: 12, color: T.red1, marginTop: 4 }}>{msg}</div>;
  };

  // `payment` carries the PayPal capture's references into the register op
  // (contract: PublicRegistrationInput.paymentIntentId + orderAccessToken —
  // the server re-verifies "paid" itself, never trusting the client claim).
  // Returns success so the PayPal path can distinguish capture-ok/register-fail.
  /* paymentCredentialProvider (2026-08-31, PP; renamed 2026-09-01): the register
   * op carries ONE reference slot
   * (paymentIntentId) but three providers write into it, with no discriminator —
   * so the live gate, scoped `provider = 'stripe'`, answered 404 on every PayPal
   * and Adyen paid registration. The provider now rides alongside the id, and the
   * contract refuses a body with paymentIntentId and no paymentCredentialProvider: a loud
   * refusal beats an optional field that defaults to Stripe and rebuilds the same
   * bug wearing a client-bug costume. Callers pass it literally — each checkout
   * branch knows its own provider statically, so it can never be inferred wrongly.
   *
   * NAMED `paymentCredentialProvider`, not `paymentProvider` (agreed 2026-09-01):
   * the contract already has `paymentProvider` on the EVENT schema ('stripe' |
   * 'none' — which provider the event checks out with). Same name on a different
   * object means wiring the wrong one typechecks and silently does nothing. This
   * name says what the value IS: the provider that issued the credential sitting
   * in the paymentIntentId slot. */
  const register = async (payment?: { orderId: string; orderAccessToken: string; label: string; provider: "stripe" | "paypal" | "adyen" }): Promise<boolean> => {
    if (busy) return false;
    if (!eventId) { setErr("Registration isn't available right now. Try again in a moment."); return false; }
    setBusy(true); setErr(null);
    try {
      const byKey = (k: string) => { const f = fields.find((x) => x.fieldKey === k); return f ? String(vals[f.key] ?? "").trim() : ""; };
      const companyField = fields.find((x) => !x.fieldKey && /company|organis|organiz/i.test(x.label));
      // §2.6 (2026-07-26): answers[] IS declared on the contract and typed
      // client today ({questionId, value}[]) — send every collected custom-
      // question value; the required-field GATE above enforces them before
      // submit. questionId = the field key (resolveRegistrationFields keys
      // custom questions by q.id); the c.showCo company field stays on the
      // dedicated `company` input, never duplicated into answers.
      // paymentIntentId/orderAccessToken are the SAME additive ask for payment
      // references (REQ-045; the contract's publishing registration input already
      // declares both — the server verifies "paid" itself, never trusting the
      // client; the api-client input interface doesn't yet — REQ-045c).
      type RegistrationWithPayment = Parameters<typeof submitPublicEventRegistration>[0] & { paymentIntentId?: string; paymentCredentialProvider?: "stripe" | "paypal" | "adyen"; orderAccessToken?: string; answers?: { questionId: string; value: string }[] };
      const answers = fields
        .filter((f) => !f.fieldKey && f !== companyField)
        .map((f) => ({ questionId: f.key, value: String(vals[f.key] ?? "").trim() }))
        .filter((a) => a.value !== "");
      const payload: RegistrationWithPayment = {
        eventId,
        ticketId: selectedTier?.id != null ? String(selectedTier.id) : undefined,
        firstName: byKey("firstName"),
        lastName: byKey("lastName"),
        email: byKey("email"),
        phone: byKey("phone") || undefined,
        company: companyField ? (String(vals[companyField.key] ?? "").trim() || undefined) : undefined,
        paymentIntentId: payment?.orderId,
        paymentCredentialProvider: payment?.provider,
        orderAccessToken: payment?.orderAccessToken,
        answers: answers.length ? answers : undefined,
      };
      /* PDD-084 (2026-09-01): the paid-registration money gate answers on
       * error.code, and the three refusals it can return are NOT one failure —
       * two of them share 409 and the code is the only thing separating a retry
       * from a hard stop.
       *
       *   402 PAYMENT_REQUIRED     — no credentials on a priced ticket. Back to
       *                              the payment step; a card is genuinely due.
       *   409 PAYMENT_CONFLICT     — the intent does not match this registration.
       *                              Hard stop: retrying re-sends the same
       *                              mismatch, and re-prompting would take a
       *                              second payment for one ticket.
       *   409 PAYMENT_UNAVAILABLE  — capture SUCCEEDED and the webhook has not
       *                              landed. The money may already be taken, so
       *                              this must never surface as failure and must
       *                              never re-prompt for a card. Silent retry IS
       *                              the correct response.
       *   404 NOT_FOUND           — the intent does not resolve for this event.
       *                              Hard stop, like CONFLICT. Deliberately
       *                              indistinguishable between unknown intent,
       *                              wrong order token and wrong event so intent
       *                              ids cannot be probed — so we cannot say
       *                              which, and must not guess in the copy.
       *   422 TICKET_SELECTION_REQUIRED — no ticket named where every tier is
       *                              priced. Back to the form to pick one.
       *
       * Deliberately NOT special-cased: "PayPal and Adyen always 404". True only
       * until the provider-scoped gate is widened; encoding it here would turn a
       * temporary backend state into permanent client behaviour, and it would
       * keep firing after the fix. 404 is treated as what it means. */
      const res = await withPaymentRetry(() => submitPublicEventRegistration(payload));
      if (res.alreadyRegistered === true) { setAlreadyRegistered(true); return true; } /* Already-registered (JAI-2279, absorbed 2026-10-05). . */
      if (payment) setPaidLabel(payment.label);
      else if (needsPayment) setPaidLabel(`${regTierPrice(selectedTier)} ${t("landing.builderHelpers.testPayment", "test payment")}`);
      setDoneEmail(byKey("email"));
      // JAI-579: read passPkpassUrl off the first ticket link. Narrowed locally
      // because this tree's vendored api-client predates PublicTicketLink —
      // once the typed kit copy lands, this cast comes out and the field is
      // read straight off res. Absent field → stays null → panel unchanged.
      const links = (res as { ticketLinks?: { passPkpassUrl?: string | null }[] }).ticketLinks;
      const pass = Array.isArray(links) ? links.find((l) => typeof l?.passPkpassUrl === "string" && l.passPkpassUrl) : undefined;
      setPassUrl(pass?.passPkpassUrl ?? null);
      setDone(res.confirmationNumber || "confirmed");
      return true;
    } catch (e) {
      console.warn("submitPublicEventRegistration failed:", e);
      const code = String((e as { code?: string }).code ?? "");
      const status = (e as { status?: number }).status;
      if (code === "PAYMENT_REQUIRED") {
        setStep("payment");
        setErr(t("landing.builderHelpers.paymentRequiredForThisTicket", "This ticket needs to be paid for before you're registered — complete the payment below."));
        return false;
      }
      if (code === "PAYMENT_UNAVAILABLE") {
        /* Retries are exhausted, not failed. The capture succeeded; only the
           confirmation is late. Say that, and say do-not-pay-again — the one
           thing a guest will otherwise do. */
        setErr(t("landing.builderHelpers.paymentConfirmationPending", "Your payment went through. We're still waiting for your bank to confirm it, so your registration isn't finished yet — don't pay again. We'll email your confirmation as soon as it lands, usually within a few minutes."));
        return false;
      }
      if (code === "PAYMENT_CONFLICT" || code === "NOT_FOUND" || (status === 404 && !code)) {
        setErr(t("landing.builderHelpers.paymentCannotBeMatched", "We can't match this payment to this registration, so we've stopped rather than risk charging you twice. Please contact the organizer with your payment reference — don't pay again."));
        return false;
      }
      if (code === "TICKET_SELECTION_REQUIRED") {
        setStep("form");
        setErr(t("landing.builderHelpers.chooseATicketFirst", "Please choose a ticket before registering."));
        return false;
      }
      setErr(code === "event_not_published"
        ? "This event isn't published yet — registration is closed."
        : status === 422 && (e as { message?: string }).message
          ? String((e as { message?: string }).message)
          : "Couldn't complete your registration. Nothing was submitted — please try again.");
      return false;
    } finally { setBusy(false); }
  };

  const onRegister = () => {
    if (mode !== "live" || busy) return;
    if (!allValid) { setTouched(Object.fromEntries(fields.map((f) => [f.key, true]))); return; } // ghosted — reveal what's missing, never submit
    if (needsPayment) {
      // Defect fix 2026-07-24: never reach the payment step (or the legacy card
      // fallback) on an unknown provider — a failed config fetch previously fell
      // through to a no-payment register() that showed "paid" without charging.
      if (payCfgStatus === "loading") { setErr(t("landing.builderHelpers.paymentOptionsStillLoading", "Payment options are still loading — one moment, then try again.")); return; }
      if (payCfgStatus === "error") { setErr(t("landing.builderHelpers.paymentOptionsFailed", "We couldn't load the payment options for this event — retrying now. Please try again in a moment.")); setPayCfgEpoch((n) => n + 1); return; }
      setStep("payment"); setErr(null); return; // ui-local — step nav to the payment screen (Req 4)
    }
    void register();
  };

  /* Already-registered (JAI-2279, absorbed 2026-10-05): neutral already-registered state — no reference, no pass, no paid label. */
  if (alreadyRegistered) return (
    <div role="status" style={{ padding: 16, background: T.n6, border: `1px solid ${T.border}`, borderRadius: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("events.publicRegister.alreadyRegisteredTitle", "You're already registered")}</div>
      <div style={{ fontSize: 12.5, color: T.n2, marginTop: 2 }}>{t("events.publicRegister.alreadyRegisteredBody", "You're already registered for this event. Check your email for your confirmation.")}</div>
    </div>
  );

  if (done) return (
    // JAI-579 — the Wallet affordance lives INSIDE this panel, under a hairline
    // divider, because the pass belongs to the confirmation rather than sitting
    // beside it. Column layout so the divider can span the panel; the check mark
    // and message keep their original row.
    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16, background: T.green2, border: `1px solid ${T.green1}`, borderRadius: 8 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <span style={{ width: 36, height: 36, borderRadius: 999, background: T.successFill, color: T.onColor, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.check size={16}/></span>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("landing.builderHelpers.youReRegistered", "You're registered")}{" "}{selectedTier ? t("landing.builderHelpers.text15", "· {name}", { name: selectedTier.name }) : ""}.</div>
        <div style={{ fontSize: 12.5, color: T.n2, marginTop: 2 }}>{paidLabel ? t("landing.builderHelpers.text7", "{paidLabel} · ", { paidLabel }) : ""}{t("landing.builderHelpers.confirmationSentTo", "Confirmation sent to")}{" "}{doneEmail || t("landing.builderHelpers.text17", "your email")} {t("landing.builderHelpers.reference", "· reference")}<span style={{ fontFamily: T.fontMono }}>{done}</span></div>
      </div>
      </div>
      {passUrl ? (
        /* Rendered only when the response carried passPkpassUrl. No fallback
           text, no disabled state, no reserved gap when it is absent — the
           panel must look deliberate on a tenant without Wallet, not like
           something failed to load.
           The link is a FILE DOWNLOAD (application/vnd.apple.pkpass), not a
           navigation, hence `download`. It carries the management token in its
           query string, so it is treated as any credential-bearing URL: no
           analytics binding, no logging, and referrer suppressed. */
        <>
          <div aria-hidden="true" style={{ height: 1, background: T.green1, opacity: 0.5 }} />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <a
              href={passUrl}
              download
              rel="noreferrer"
              referrerPolicy="no-referrer"
              aria-label={t("landing.builderHelpers.ariaLabel", "Add to Apple Wallet")}
              style={{
                /* The badge is Apple's licensed artwork, served byte-identical
                   from the app's static tree (PR #842) — no bundler, no
                   optimiser, nothing between Apple's bytes and the browser. An
                   SVG optimiser on this path strips the <style> block and the
                   badge renders as a solid black slab: geometry intact, wordmark
                   and logo invisible because every class falls to default black.
                   That failure is invisible to reading the file and only shows
                   up in a render.

                   Nothing here may recolour, shadow, rotate or animate the
                   artwork — no filter, no transform, no hover recolour. A
                   pressed or hover treatment belongs on this anchor, never on
                   the image.

                   Height is our 44px hit-target floor (Apple publishes no
                   absolute minimum); width follows the artwork's own 3.163
                   ratio to 139.2px. Padding is the 0.1x-height clear space,
                   written against the same 44 so the two move together if the
                   height ever changes. */
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                padding: 44 * 0.1, boxSizing: "content-box",
                maxWidth: "100%", textDecoration: "none",
              }}
            >
              <img
                src={`/wallet-badge/${currentLocale()}.svg`}
                onError={(e) => {
                  /* Anything outside the 34 shipped locales falls back to en.
                     Guarded so a missing en.svg cannot loop. de-CH and qps-ploc
                     are real files at those names (DE and US_UK artwork
                     respectively — Apple ships no variant for either), so they
                     resolve directly and no mapping table is needed. */
                  const img = e.currentTarget;
                  if (img.dataset.fallback) return;
                  img.dataset.fallback = "1";
                  img.src = "/wallet-badge/en.svg";
                }}
                alt=""
                height={44}
                style={{ height: 44, width: "auto", maxWidth: "100%", display: "block" }}
              />
            </a>
            <div style={{ fontSize: 12.5, color: T.n2, textAlign: "center" }}>
              {t("landing.builderHelpers.text16", "Save your ticket for check-in. Downloads a pass file.")}</div>
          </div>
        </>
      ) : null}
    </div>
  );

  // ── Req 4: payment-capture placeholder — same-page step transition. A
  // believable card layout; "Pay" awaits the REAL register op, no PSP call.
  if (step === "payment" && mode === "live") {
    const amount = regTierPrice(selectedTier);
    // Defect fix 2026-07-24: blocking guard — no provider widget and NO legacy
    // card fallback until the payment config has genuinely loaded ("ok").
    if (payCfgStatus !== "ok") {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* ui-raw: renders the CUSTOMER-FACING published page inside the builder preview, filled with the tenant colour (theme.brand) — Studio Button inks from our brand token and must not render here */}
          <button onClick={() => setStep("form")} style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: theme.brand, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
            <Icon.arrow dir="left" size={11}/> {t("landing.builderHelpers.backToYourDetails", "Back to your details")}</button>
          {payCfgStatus === "loading" ? (
            <div style={{ padding: "14px 16px", background: T.n6, border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 12.5, color: T.soft }}>
              {t("landing.builderHelpers.loadingPaymentOptions", "Loading payment options…")}</div>
          ) : (
            <div role="alert" style={{ display: "flex", flexDirection: "column", gap: 10, padding: "14px 16px", background: alpha(T.red1, 7), border: `1px solid ${alpha(T.red1, 30)}`, borderRadius: 6 }}>
              <div style={{ fontSize: 12.5, lineHeight: 1.5, color: T.n1 }}>{t("landing.builderHelpers.paymentOptionsFailedBlocking", "We couldn't load the payment options for this event, so checkout is paused — you haven't been charged.")}</div>
              <Btn size="sm" variant="secondary" style={{ alignSelf: "flex-start" }} onClick={() => setPayCfgEpoch((n) => n + 1)}>
                {t("landing.builderHelpers.retryLoadingPaymentOptions", "Retry")}</Btn>
            </div>
          )}
        </div>
      );
    }
    // REQ-045 provider chooser: PayPal-configured events get Smart Buttons —
    // no card fields (PayPal owns its popup). A PayPal provider that isn't
    // verified-receivable keeps checkout honestly disabled. Stripe (or no
    // config) falls through to the existing card payment step, unchanged.
    // REQ-060 provider chooser seam: Adyen-configured events get the session-
    // driven Drop-in — no Voyage card fields (Adyen owns its iframe). An Adyen
    // provider that isn't verified-receivable keeps checkout honestly disabled.
    if (eventId && (adyenVerified || adyenUnready)) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* ui-raw: renders the CUSTOMER-FACING published page inside the builder preview, filled with the tenant colour (theme.brand) — Studio Button inks from our brand token and must not render here */}
          <button onClick={() => setStep("form")} style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: theme.brand, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
            <Icon.arrow dir="left" size={11}/> {t("landing.builderHelpers.backToYourDetails", "Back to your details")}</button>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>{t("landing.builderHelpers.completeYourPayment", "Complete your payment")}</div>
            <div style={{ fontSize: 13, color: T.n2, marginTop: 4 }}>{event?.title ? t("landing.builderHelpers.text18", "{title} ·", { title: event.title }) : ""}{selectedTier?.name || t("landing.builderHelpers.text10", "Ticket")}</div>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "12px 14px", background: alpha(theme.brand, "14"), border: `1px solid ${alpha(theme.brand, "44")}`, borderRadius: 6 }}>
            <span style={{ fontSize: 12.5, color: T.n1, fontWeight: 600 }}>{t("landing.builderHelpers.amountDue", "Amount due")}</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: T.n1 }}>{amount}</span>
          </div>
          {adyenVerified ? (
            <AdyenCheckout
              eventId={eventId}
              ticketId={selectedTier?.id != null ? String(selectedTier.id) : undefined}
              quantity={1}
              onPaid={async (s) => {
                // sessionId rides the register op's existing paymentIntentId slot
                // (the Stripe-analog reference; the server re-verifies "paid"),
                // now discriminated by paymentCredentialProvider so the gate resolves it
                // against Adyen rows instead of 404ing on the Stripe-only scope.
                const ok = await register({ orderId: s.sessionId, orderAccessToken: s.orderAccessToken, provider: "adyen", label: formatICU(t("landing.builderHelpers.paidWithAdyenAmount", "{amount} paid with Adyen"), { amount: formatMinor(s.amountMinor, s.currency) }) });
                if (!ok) throw new Error("registration-failed");
              }}
            />
          ) : (
            <div role="note" style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", background: T.orange2, border: `1px solid ${alpha(T.orange1, "55")}`, borderRadius: 6, fontSize: 12.5, color: T.n1, lineHeight: 1.5 }}>
              <Icon.info size={13} style={{ color: T.orange1, flexShrink: 0, marginTop: 2 }}/>
              {t("landing.builderHelpers.adyenPendingVerificationNote", "Online payment isn't available for this event yet — the organizer's Adyen account is still being verified. Please check back shortly.")}</div>
          )}
        </div>
      );
    }
    if (eventId && (paypalVerified || paypalUnready)) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* ui-raw: renders the CUSTOMER-FACING published page inside the builder preview, filled with the tenant colour (theme.brand) — Studio Button inks from our brand token and must not render here */}
          <button onClick={() => setStep("form")} style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: theme.brand, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
            <Icon.arrow dir="left" size={11}/> {t("landing.builderHelpers.backToYourDetails", "Back to your details")}</button>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>{t("landing.builderHelpers.completeYourPayment", "Complete your payment")}</div>
            <div style={{ fontSize: 13, color: T.n2, marginTop: 4 }}>{event?.title ? t("landing.builderHelpers.text18", "{title} ·", { title: event.title }) : ""}{selectedTier?.name || t("landing.builderHelpers.text10", "Ticket")}</div>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "12px 14px", background: alpha(theme.brand, "14"), border: `1px solid ${alpha(theme.brand, "44")}`, borderRadius: 6 }}>
            <span style={{ fontSize: 12.5, color: T.n1, fontWeight: 600 }}>{t("landing.builderHelpers.amountDue", "Amount due")}</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: T.n1 }}>{amount}</span>
          </div>
          {paypalVerified ? (
            <PayPalCheckout
              eventId={eventId}
              ticketId={selectedTier?.id != null ? String(selectedTier.id) : undefined}
              quantity={1}
              sdkConfig={payCfg!.paypal!}
              onPaid={async (o) => {
                const ok = await register({ orderId: o.orderId, orderAccessToken: o.orderAccessToken, provider: "paypal", label: formatICU(t("landing.builderHelpers.paidWithPaypalAmount", "{amount} paid with PayPal"), { amount: formatMinor(o.amountMinor, o.currency) }) });
                if (!ok) throw new Error("registration-failed");
              }}
            />
          ) : (
            <div role="note" style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", background: T.orange2, border: `1px solid ${alpha(T.orange1, "55")}`, borderRadius: 6, fontSize: 12.5, color: T.n1, lineHeight: 1.5 }}>
              <Icon.info size={13} style={{ color: T.orange1, flexShrink: 0, marginTop: 2 }}/>
              {t("landing.builderHelpers.paypalAwaitingConfirmationNote", "Online payment isn't available for this event yet — the organizer's PayPal account is awaiting confirmation. Please check back shortly.")}</div>
          )}
        </div>
      );
    }
    const cardDigits = card.number.replace(/\D/g, "");
    const payReady = cardDigits.length >= 13 && /^\d{2} \/ \d{2}$/.test(card.exp) && card.cvc.length >= 3;
    const fmtCardNum = (s: string) => { const d = s.replace(/\D/g, "").slice(0, 16); return (d.match(/.{1,4}/g) || []).join("  "); };
    const fmtExp = (s: string) => { const d = s.replace(/\D/g, "").slice(0, 4); return d.length > 2 ? `${d.slice(0, 2)} / ${d.slice(2)}` : d; };
    const payLbl = (txt: string) => <div style={{ fontSize: 13, color: T.n1, fontWeight: 500, marginBottom: 4 }}>{txt}<span aria-hidden="true" style={{ color: T.red1 }}> *</span></div>;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* ui-raw: renders the CUSTOMER-FACING published page inside the builder preview, filled with the tenant colour (theme.brand) — Studio Button inks from our brand token and must not render here */}
        <button onClick={() => setStep("form")} style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: theme.brand, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
          <Icon.arrow dir="left" size={11}/> {t("landing.builderHelpers.backToYourDetails", "Back to your details")}</button>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>{t("landing.builderHelpers.completeYourPayment", "Complete your payment")}</div>
          <div style={{ fontSize: 13, color: T.n2, marginTop: 4 }}>{event?.title ? t("landing.builderHelpers.text18", "{title} ·", { title: event.title }) : ""}{selectedTier?.name || t("landing.builderHelpers.text10", "Ticket")}</div>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "12px 14px", background: alpha(theme.brand, "14"), border: `1px solid ${alpha(theme.brand, "44")}`, borderRadius: 6 }}>
          <span style={{ fontSize: 12.5, color: T.n1, fontWeight: 600 }}>{t("landing.builderHelpers.amountDue", "Amount due")}</span>
          <span style={{ fontSize: 18, fontWeight: 800, color: T.n1 }}>{amount}</span>
        </div>
        <div>
          {payLbl("Card number")}
          <input value={card.number} disabled={busy} inputMode="numeric" autoComplete="cc-number" placeholder="1234  1234  1234  1234" onChange={(e) => setCard((s) => ({ ...s, number: fmtCardNum(e.target.value) }))} aria-label={t("landing.builderHelpers.cardNumber", "Card number")} style={inputStyle()}/>
        </div>
        <div className="lp-pay-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <div>
            {payLbl("Expiry")}
            <input value={card.exp} disabled={busy} inputMode="numeric" autoComplete="cc-exp" placeholder={t("landing.builderHelpers.mmYy", "MM / YY")} onChange={(e) => setCard((s) => ({ ...s, exp: fmtExp(e.target.value) }))} aria-label={t("landing.builderHelpers.expiry", "Expiry")} style={inputStyle()}/>
          </div>
          <div>
            {payLbl("CVC")}
            <input value={card.cvc} disabled={busy} inputMode="numeric" autoComplete="cc-csc" placeholder="•••" onChange={(e) => setCard((s) => ({ ...s, cvc: e.target.value.replace(/\D/g, "").slice(0, 4) }))} aria-label={t("landing.builderHelpers.cvc", "CVC")} style={inputStyle()}/>
          </div>
          <div>
            <div style={{ fontSize: 13, color: T.n1, fontWeight: 500, marginBottom: 4 }}>{t("landing.builderHelpers.zipPostcode", "ZIP / postcode")}</div>
            <input value={card.zip} disabled={busy} autoComplete="postal-code" placeholder="02139" onChange={(e) => setCard((s) => ({ ...s, zip: e.target.value.slice(0, 10) }))} aria-label={t("landing.builderHelpers.zipOrPostcode", "ZIP or postcode")} style={inputStyle()}/>
          </div>
        </div>
        {err ? <div style={{ fontSize: 12.5, color: T.red1, lineHeight: 1.45 }}>{err}</div> : null}
        {/* PDD-084 (2026-09-01): this card form is SIMULATED — it never mints a
            payment intent, so a live register() from here carries no payment
            credentials and earns 402 PAYMENT_REQUIRED on any priced tier. That
            is correct on both sides. Rather than submit into a guaranteed
            refusal, a live paid checkout with no real provider configured says
            so, in the same honest-disable treatment the unverified PayPal and
            Adyen branches use. The builder preview keeps the simulated card:
            design-time is exactly where a fake card belongs. */}
        {mode === "live" && needsPayment ? (
          <div role="note" style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 12px", background: T.orange2, border: `1px solid ${alpha(T.orange1, "55")}`, borderRadius: 6, fontSize: 12.5, color: T.n1, lineHeight: 1.5 }}>
            <Icon.info size={13} style={{ color: T.orange1, flexShrink: 0, marginTop: 2 }}/>
            {t("landing.builderHelpers.paidCheckoutNotConfiguredNote", "Online payment isn't available for this event yet — the organizer hasn't finished setting up card payments. You haven't been charged. Please check back shortly.")}</div>
        ) : (
        /* ui-raw: renders the CUSTOMER-FACING published page inside the builder preview, filled with the tenant colour (theme.brand) — Studio Button inks from our brand token and must not render here */
        <button
          onClick={payReady && !busy ? () => { void register(); } : undefined}
          aria-disabled={!payReady || busy || undefined}
          title={payReady ? undefined : t("landing.landingBuilderHelpers.completeTheCardDetailsToPay", "Complete the card details to pay")}
          style={{ marginTop: 2, background: theme.brand, color: T.white, fontWeight: 700, padding: "12px 18px", borderRadius: 4, border: "none", cursor: payReady && !busy ? "pointer" : "not-allowed", fontSize: 13, opacity: payReady || busy ? 1 : 0.45, fontFamily: "inherit", transition: "opacity 120ms" }}>
          {busy ? t("landing.landingBuilderHelpers.processing", "Processing…") : t("landing.landingBuilderHelpers.payAmount", "Pay {value0}", { value0: amount })}
        </button>
        )}
        {mode === "live" && needsPayment ? null : (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.soft }}>
          <Icon.lock size={11}/> {t("landing.builderHelpers.testPaymentYourCardWonTBeCharged", "Test payment — your card won't be charged.")}</div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {mode === "draft" ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "10px 12px", background: T.orange2, border: `1px solid ${alpha(T.orange1, "55")}`, borderRadius: 6, fontSize: 12.5, color: T.n1 }}>
          <Icon.info size={13} style={{ color: T.orange1, flexShrink: 0 }}/> {t("landing.builderHelpers.registrationOpensWhenThisEventIsPublished", "Registration opens when this event is published.")}</div>
      ) : null}
      {selectedTier ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: alpha(theme.brand, "14"), border: `1px solid ${alpha(theme.brand, "44")}`, borderRadius: 6 }}>
          <Icon.ticket size={14} style={{ color: theme.brand, flexShrink: 0 }}/>
          <span style={{ fontSize: 12.5, color: T.n1, flex: 1, minWidth: 0 }}>{multiTier ? t("landing.builderHelpers.text9", "Selected ticket") : t("landing.builderHelpers.text10", "Ticket")} · <b>{selectedTier.name}</b></span>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{regTierPrice(selectedTier)}</span>
        </div>
      ) : null}
      {(() => {
        const nodes: React.ReactNode[] = [];
        for (let i = 0; i < fields.length; i++) {
          const f = fields[i];
          const nxt = fields[i + 1];
          if (f.fieldKey === "firstName" && nxt?.fieldKey === "lastName") {
            nodes.push(
              <div key="name" className="lp-name-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[f, nxt].map((g) => <div key={g.key}>{labelRow(g)}{fieldControl(g)}{invalidMsg(g)}</div>)}
              </div>,
            );
            i++;
            continue;
          }
          nodes.push(
            <div key={f.key}>
              {labelRow(f)}
              {fieldControl(f)}
              {f.type !== "check" && f.help ? <div style={{ fontSize: 12, color: T.soft, marginTop: 4 }}>{f.help}</div> : null}
              {invalidMsg(f)}
            </div>,
          );
        }
        return nodes;
      })()}
      {err ? <div style={{ fontSize: 12.5, color: T.red1, lineHeight: 1.45 }}>{err}</div> : null}
      {/* ui-raw: renders the CUSTOMER-FACING published page inside the builder preview, filled with the tenant colour (theme.brand) — Studio Button inks from our brand token and must not render here */}
      <button
        onClick={mode === "live" ? onRegister : undefined}
        disabled={mode === "draft"}
        aria-disabled={ghosted || undefined}
        title={mode === "builder" ? t("landing.landingBuilderHelpers.registersGuestsOnceThePageIsPublished", "Registers guests once the page is published") : mode === "draft" ? t("landing.landingBuilderHelpers.thisEventIsADraftRegistrationIsnTOpenYet", "This event is a draft — registration isn't open yet") : ghosted ? t("landing.landingBuilderHelpers.completeTheRequiredFieldsToRegister", "Complete the required fields to register") : undefined}
        style={{ marginTop: 4, background: mode === "draft" ? T.n4 : theme.brand, color: T.white, fontWeight: 700, padding: "12px 18px", borderRadius: 4, border: "none", cursor: mode === "draft" ? "default" : ghosted ? "not-allowed" : "pointer", fontSize: 13, opacity: ghosted ? 0.45 : 1, fontFamily: "inherit", transition: "opacity 120ms" }}>
        {busy ? t("landing.landingBuilderHelpers.submitting", "Submitting…") : needsPayment && mode !== "draft" ? t("landing.landingBuilderHelpers.continueToPayment", "Continue to payment") : String(c.cta || "Register")}
      </button>
      {needsPayment && mode !== "draft" ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.soft }}>
          <Icon.lock size={11}/> {regTierPrice(selectedTier)} {t("landing.builderHelpers.paymentDetailsOnTheNextStep", "· payment details on the next step.")}</div>
      ) : null}
      {needsPayment && mode === "builder" ? (
        // Builder preview of the payment method — ALWAYS a disabled placeholder,
        // never a live PayPal button or Adyen Drop-in. A provider tile shows only
        // when that provider is configured+verified.
        adyenVerified ? (
          <div aria-disabled="true" style={{ marginTop: 2, height: 42, borderRadius: 4, background: alpha("#0ABF53", "22"), border: `1px dashed ${T.n4}`, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 13, color: T.n2 }}>
            <b style={{ fontWeight: 800, color: "#0ABF53", opacity: 0.7 }}>{t("landing.builderHelpers.text11", "Adyen")}</b>
            <span style={{ fontSize: 11.5, color: T.soft }}>{t("landing.builderHelpers.adyenCheckoutActivatesOnPublished", "checkout — activates on the published page")}</span>
          </div>
        ) : paypalVerified ? (
          <div aria-disabled="true" style={{ marginTop: 2, height: 42, borderRadius: 4, background: alpha("#FFC439", "66"), border: `1px dashed ${T.n4}`, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 13, color: T.n2 }}>
            <b style={{ fontStyle: "italic", fontWeight: 800, color: "#003087", opacity: 0.55 }}>{t("landing.builderHelpers.text12", "PayPal")}</b>
            <span style={{ fontSize: 11.5, color: T.soft }}>{t("landing.builderHelpers.paypalCheckoutActivatesOnPublished", "checkout — activates on the published page")}</span>
          </div>
        ) : (
          <div aria-disabled="true" style={{ marginTop: 2, height: 42, borderRadius: 4, background: T.n6, border: `1px dashed ${T.n4}`, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 12, color: T.soft }}>
            <Icon.lock size={11}/> {t("landing.builderHelpers.cardPaymentStepActivatesOnPublished", "Card payment step — activates on the published page")}</div>
        )
      ) : null}
    </div>
  );
}

// ui-raw: same theme.brand-driven live-rendering constraint as SignupBlock —
// see the note above.
function ChatBlock({ c, theme, mode }: { c: Record<string, unknown>; theme: LandingTheme; mode: LandingRenderMode }) {
  const t = useT();
  const kind = String(c.recipientKind || "staff");
  const target = String(c.recipientEmail || c.fallbackEmail || "");
  const label = kind === "ai" ? "AI assistant" : (String(c.recipientLabel || "") || "our team");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const disabled = mode !== "live";
  const canSend = kind !== "ai" && !!target && !!msg.trim();
  const inputStyle: React.CSSProperties = { height: 38, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 6, background: disabled ? T.n6 : T.white, fontSize: 13, color: disabled ? T.n3 : T.n1, fontFamily: "inherit", width: "100%", boxSizing: "border-box" };
  const send = () => {
    if (disabled || !canSend) return;
    const subject = encodeURIComponent(`Chat request${c.title ? ` — ${String(c.title)}` : ""}`);
    const body = encodeURIComponent(`From: ${name || "Guest"}${email ? ` <${email}>` : ""}\n\n${msg}`);
    window.location.href = `mailto:${target}?subject=${subject}&body=${body}`;
  };
  return (
    <div style={{ maxWidth: 520, margin: "0 auto", width: "100%", boxSizing: "border-box", border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden", background: T.white }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: theme.brand, color: T.white }}>
        <span style={{ width: 30, height: 30, borderRadius: 999, background: "rgba(255,255,255,0.2)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{kind === "ai" ? <Icon.star size={15}/> : <Icon.msg size={15}/>}</span>
        <div><div style={{ fontSize: 14, fontWeight: 700 }}>{String(c.title || "Chat")}</div><div style={{ fontSize: 11.5, opacity: 0.85 }}>{t("landing.builderHelpers.with", "with")}{" "}{label}</div></div>
      </div>
      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        {c.greeting ? <div style={{ alignSelf: "flex-start", maxWidth: "85%", background: T.n6, borderRadius: "10px 10px 10px 2px", padding: "8px 12px", fontSize: 13, color: T.n1 }}>{String(c.greeting)}</div> : null}
        {kind === "ai" ? (
          <div style={{ fontSize: 12.5, color: T.soft, lineHeight: 1.5, padding: "8px 10px", background: T.orange2, border: `1px solid ${alpha(T.orange1, "55")}`, borderRadius: 8 }}>{t("landing.builderHelpers.theAiAssistantConnectsOnceLiveChatIsEnabledForYourWorkspace", "The AI assistant connects once live chat is enabled for your workspace.")}</div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <input value={name} disabled={disabled} placeholder={t("landing.builderHelpers.yourName", "Your name")} onChange={(e) => setName(e.target.value)} style={inputStyle}/>
              <input value={email} disabled={disabled} placeholder={t("landing.builderHelpers.email", "Email")} onChange={(e) => setEmail(e.target.value)} style={inputStyle}/>
            </div>
            <textarea value={msg} disabled={disabled} rows={3} placeholder={t("landing.builderHelpers.typeYourMessage", "Type your message…")} onChange={(e) => setMsg(e.target.value)} style={{ ...inputStyle, height: "auto", padding: "10px 12px", resize: "vertical", lineHeight: 1.5 }}/>
            {mode === "live" && !target ? <div style={{ fontSize: 12, color: T.soft }}>{t("landing.builderHelpers.addARecipientEmailInTheInspectorToEnableSending", "Add a recipient email in the inspector to enable sending.")}</div> : null}
            {/* ui-raw: renders the CUSTOMER-FACING published page inside the builder preview, filled with the tenant colour (theme.brand) — Studio Button inks from our brand token and must not render here */}
            <button onClick={send} aria-disabled={!canSend || undefined}
              style={{ background: theme.brand, color: T.white, fontWeight: 700, padding: "10px 16px", borderRadius: 6, border: "none", cursor: canSend ? "pointer" : "not-allowed", fontSize: 13, opacity: canSend ? 1 : 0.5, fontFamily: "inherit" }}>{t("landing.builderHelpers.sendMessage", "Send message")}</button>
          </>
        )}
      </div>
    </div>
  );
}

// ui-raw: same theme.brand-driven live-rendering constraint as SignupBlock —
// see the note above.
function ContactBlock({ c, theme, mode }: { c: Record<string, unknown>; theme: LandingTheme; mode: LandingRenderMode }) {
  const t = useT();
  const email = String(c.email || "");
  const phone = String(c.phone || "");
  const [name, setName] = useState("");
  const [from, setFrom] = useState("");
  const [msg, setMsg] = useState("");
  const disabled = mode !== "live";
  const canSend = !!email && !!msg.trim();
  const inputStyle: React.CSSProperties = { height: 38, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 6, background: disabled ? T.n6 : T.white, fontSize: 13, color: disabled ? T.n3 : T.n1, fontFamily: "inherit", width: "100%", boxSizing: "border-box" };
  const send = () => {
    if (disabled || !canSend) return;
    const subject = encodeURIComponent(`Question${c.title ? ` — ${String(c.title)}` : ""}`);
    const body = encodeURIComponent(`From: ${name || "Guest"}${from ? ` <${from}>` : ""}\n\n${msg}`);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };
  const linkBtn = (href: string, icon: React.ReactNode, text: string) => (
    <a href={disabled ? undefined : href} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.white, color: T.n1, fontSize: 13, fontWeight: 600, textDecoration: "none" }}>{icon}{text}</a>
  );
  return (
    <div style={{ maxWidth: 560, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
      {c.title ? <div style={{ fontSize: 18, fontWeight: 700, color: T.n1, marginBottom: 6 }}>{String(c.title)}</div> : null}
      {c.blurb ? <div style={{ fontSize: 13.5, color: T.n2, lineHeight: 1.55, marginBottom: 14 }}>{String(c.blurb)}</div> : null}
      {(email || phone) ? (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: c.showForm ? 16 : 0 }}>
          {email ? linkBtn(`mailto:${email}`, <Icon.mail size={14} style={{ color: theme.brand }}/>, email) : null}
          {phone ? linkBtn(`tel:${phone.replace(/[^0-9+]/g, "")}`, <Icon.phone size={14} style={{ color: theme.brand }}/>, phone) : null}
        </div>
      ) : null}
      {c.showForm !== false && email ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <input value={name} disabled={disabled} placeholder={t("landing.builderHelpers.yourName", "Your name")} onChange={(e) => setName(e.target.value)} style={inputStyle}/>
            <input value={from} disabled={disabled} placeholder={t("landing.builderHelpers.email", "Email")} onChange={(e) => setFrom(e.target.value)} style={inputStyle}/>
          </div>
          <textarea value={msg} disabled={disabled} rows={4} placeholder={t("landing.builderHelpers.yourQuestion", "Your question…")} onChange={(e) => setMsg(e.target.value)} style={{ ...inputStyle, height: "auto", padding: "10px 12px", resize: "vertical", lineHeight: 1.5 }}/>
          {/* ui-raw: renders the CUSTOMER-FACING published page inside the builder preview, filled with the tenant colour (theme.brand) — Studio Button inks from our brand token and must not render here */}
          <button onClick={send} aria-disabled={!canSend || undefined}
            style={{ alignSelf: "flex-start", background: theme.brand, color: T.white, fontWeight: 700, padding: "10px 18px", borderRadius: 6, border: "none", cursor: canSend ? "pointer" : "not-allowed", fontSize: 13, opacity: canSend ? 1 : 0.5, fontFamily: "inherit" }}>{t("common.actions.send", "Send")}</button>
        </div>
      ) : (c.showForm !== false && !email ? <div style={{ fontSize: 12.5, color: T.soft }}>{t("landing.builderHelpers.addAContactEmailInTheInspectorToEnableTheMessageForm", "Add a contact email in the inspector to enable the message form.")}</div> : null)}
    </div>
  );
}

// ui-raw: RenderedBlock is the SAME renderer for the builder canvas and the
// live published page — its ticket-tier buttons, hero/CTA buttons, and block
// toolbar (miniBtn) are all styled by the runtime `theme.brand`/`theme.hero`
// per-event brand color. jrni-ui's Btn/IconBtn hardcode fixed brand tokens
// with no dynamic-color seam, so converting them would strip per-event
// branding — a core requirement of this WYSIWYG renderer (see the
// "Full-bleed layout" + Req-2/3/4/5 comments throughout this function).
// Left raw for parity; the block toolbar buttons reuse the marked `miniBtn`
// style above.
function RenderedBlock({ block, theme, event, mode = "builder", eventId, onOpenAttend, hubEvents, selectedTierId, onSelectTier, selected, selectedUid, dragOverKey, onCellDrop, onSelect, onRemove, onMove, onDuplicate, dragOver, onDragOverBlock, onDrop, onDragStartBlock, renderCustom }: {
  block: LandingBlock; theme: LandingTheme; event?: PublicEventDetailLike | null; mode?: LandingRenderMode; eventId?: string; onOpenAttend?: () => void;
  hubEvents?: HubEventCard[];
  selectedTierId?: string | null; onSelectTier?: (id: string) => void; selected?: boolean;
  selectedUid?: string | null; dragOverKey?: string | null; onCellDrop?: (rowUid: string, cellIdx: number) => void;
  onSelect?: (uid: string) => void; onRemove?: (uid: string) => void; onMove?: (uid: string, dir: number) => void; onDuplicate?: (uid: string) => void;
  dragOver?: boolean; onDragOverBlock?: (uid: string) => void; onDrop?: (uid: string) => void; onDragStartBlock?: (uid: string) => void;
  renderCustom?: (block: LandingBlock) => React.ReactNode;
}) {
  const t = useT();
  const translate = t;
  const wrap = (children: React.ReactNode) => (
    <div id={"lp-" + block.uid} onClick={(e) => { e.stopPropagation(); onSelect?.(block.uid); }}
      draggable={!!onDragStartBlock}
      onDragStart={(e) => { e.stopPropagation(); onDragStartBlock?.(block.uid); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOverBlock?.(block.uid); }}
      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop?.(block.uid); }}
      style={{ position: "relative", outline: selected ? `2px solid ${T.p1}` : dragOver ? `2px dashed ${T.p1}` : "2px solid transparent", outlineOffset: -2, cursor: onSelect ? "pointer" : "default" }}>
      {selected && onRemove ? (
        <div style={{ position: "absolute", top: 6, right: 6, zIndex: 2, display: "flex", gap: 4, background: T.white, border: `1px solid ${T.border}`, borderRadius: 4, padding: 2 }} onClick={(e) => e.stopPropagation()}>
          {/* ui-raw: 22px borderless nudge control in a dense inline row (miniBtn) — IconBtn is 32/40/48 with a border and chrome shadow; needs the borderless treatment (capability 1) AND a sub-32 step */}
          <button onClick={() => onMove?.(block.uid, -1)} title={t("landing.builderHelpers.up", "Up")}     style={miniBtn as React.CSSProperties}><Icon.chev dir="up" size={11}/></button>
          {/* ui-raw: 22px borderless nudge control in a dense inline row (miniBtn) — IconBtn is 32/40/48 with a border and chrome shadow; needs the borderless treatment (capability 1) AND a sub-32 step */}
          <button onClick={() => onMove?.(block.uid, +1)} title={t("landing.builderHelpers.down", "Down")}   style={miniBtn as React.CSSProperties}><Icon.chev size={11}/></button>
          {/* ui-raw: 22px borderless nudge control in a dense inline row (miniBtn) — IconBtn is 32/40/48 with a border and chrome shadow; needs the borderless treatment (capability 1) AND a sub-32 step */}
          {onDuplicate ? <button onClick={() => onDuplicate(block.uid)} title={t("landing.builderHelpers.duplicate", "Duplicate")} style={miniBtn as React.CSSProperties}><Icon.copy size={11}/></button> : null}
          {/* ui-raw: 22px borderless nudge control in a dense inline row (miniBtn) — IconBtn is 32/40/48 with a border and chrome shadow; needs the borderless treatment (capability 1) AND a sub-32 step */}
          <button onClick={() => onRemove(block.uid)}     title={t("common.actions.delete", "Delete")} style={{ ...miniBtn as object, color: T.red1 } as React.CSSProperties}><Icon.trash size={11}/></button>
        </div>
      ) : null}
      {children}
    </div>
  );
  const c = block.cfg;
  const COLORS = [T.tLightBlue, T.tPink, T.tTurquoise, T.tPurple, T.tOrange, T.tGreen, T.tYellow, T.tDarkBlue];
  if (renderCustom) { const custom = renderCustom(block); if (custom != null) return wrap(custom); }
  if (block.type === "row") {
    const cells = getRowCells(c);
    const gap = Number(c.gap) || 20;
    const stack = c.stackMobile !== false;
    const editable = !!onCellDrop;
    const pad = Number(c.pad) || 20;
    const isBandImg = c.bandKind === "image" && !!c.bandImage;
    const bandOpacity = c.bandOpacity == null ? 1 : Number(c.bandOpacity);
    const bandBase = c.bandKind === "color" ? String(c.bandBg || "transparent") : "transparent";
    return wrap(<div className="lp-sec" style={{ position: "relative", overflow: "hidden", padding: `${pad}px 32px`, background: bandBase }}>
      {isBandImg ? <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: `url(${c.bandImage}) center/cover`, opacity: bandOpacity }}/> : null}
      <div style={{ ...lpInner, position: "relative", zIndex: 1 }}>
      <div className={"lp-row" + (stack ? " lp-row-stack" : "")} style={{ display: "grid", gridTemplateColumns: rowGridCols(c), gap, alignItems: "center" }}>
        {cells.map((cell, ci) => {
          const over = dragOverKey === `cell:${block.uid}:${ci}`;
          const empty = cell.length === 0;
          return (
            <div key={ci}
              onDragOver={editable ? (e) => { e.preventDefault(); e.stopPropagation(); onDragOverBlock?.(`cell:${block.uid}:${ci}`); } : undefined}
              onDrop={editable ? (e) => { e.preventDefault(); e.stopPropagation(); onCellDrop?.(block.uid, ci); } : undefined}
              style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0,
                border: editable ? `1.5px dashed ${over ? T.p1 : (empty ? T.n4 : "transparent")}` : "none",
                borderRadius: 8, background: over ? T.p4 : "transparent", padding: editable ? 8 : 0, minHeight: editable ? 72 : undefined, transition: "background 120ms, border-color 120ms" }}>
              {cell.map((child) => (
                <RenderedBlock key={child.uid} block={child} theme={theme} event={event} mode={mode} eventId={eventId} onOpenAttend={onOpenAttend} hubEvents={hubEvents}
                  selectedTierId={selectedTierId} onSelectTier={onSelectTier}
                  selected={selectedUid === child.uid} selectedUid={selectedUid} dragOverKey={dragOverKey} onCellDrop={onCellDrop}
                  onSelect={onSelect} onRemove={onRemove} onMove={onMove} onDuplicate={onDuplicate}
                  dragOver={dragOverKey === child.uid} onDragOverBlock={onDragOverBlock} onDrop={onDrop} onDragStartBlock={onDragStartBlock} renderCustom={renderCustom}/>
              ))}
              {editable && empty ? (
                <div style={{ textAlign: "center", color: T.soft, fontSize: 12, padding: "14px 4px", pointerEvents: "none", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <Icon.plus size={13}/> {t("landing.builderHelpers.dropABlock", "Drop a block")}</div>
              ) : null}
            </div>
          );
        })}
      </div>
      </div>
    </div>);
  }
  if (block.type === "hero") {
    const isImg = c.bgKind === "image" && !!c.imageUrl;
    const imgOpacity = c.imageOpacity == null ? 1 : Number(c.imageOpacity);
    const baseBg = c.bgKind === "solid" ? String(c.solid || T.p1)
             : c.bgKind === "image" ? (c.imageUrl ? "#1b1b1f" : `linear-gradient(rgba(0,0,0,0.35),rgba(0,0,0,0.45))`)
             : `linear-gradient(135deg, ${c.from}, ${c.to})`;
    return wrap(<div className="lp-hero" style={{ position: "relative", overflow: "hidden", background: baseBg, color: T.white, padding: `${Number(c.padding)||40}px 32px`, textAlign: c.align === "center" ? "center" : "left" }}>
      {isImg ? <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: `url(${c.imageUrl}) center/cover`, opacity: imgOpacity }}/> : null}
      <div style={{ ...lpInner, position: "relative", zIndex: 1 }}>
      <Pill tone="info" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>{String(c.pill || "Event")}</Pill>
      <h1 style={{ fontSize: 32, fontWeight: 800, margin: "12px 0 6px", letterSpacing: -0.8, lineHeight: 1.05, color: String(c.headlineColor || T.white) }}>{String(c.headline || "")}</h1>
      <div className="lp-sub" style={{ fontSize: 14, color: String(c.subheadColor || T.white), opacity: c.subheadColor ? 1 : 0.92, maxWidth: 480, marginLeft: c.align === "center" ? "auto" : 0, marginRight: c.align === "center" ? "auto" : 0 }}>{String(c.subhead || "")}</div>
      {/* ui-raw: renders the CUSTOMER-FACING published page inside the builder preview, filled with the tenant colour (theme.brand) — Studio Button inks from our brand token and must not render here */}
      {c.showCTA ? <button
        disabled={mode === "draft"}
        title={mode === "builder" ? t("landing.landingBuilderHelpers.registersGuestsOnceThePageIsPublished", "Registers guests once the page is published") : mode === "draft" ? t("landing.landingBuilderHelpers.thisEventIsADraftRegistrationIsnTOpenYet", "This event is a draft — registration isn't open yet") : undefined}
        onClick={mode === "live" ? (e) => {
          // In-place registration: jump to Register's first field when the page
          // has one; otherwise fall through to the real attend.* page (never a
          // dead button). focus() brings it into view natively.
          const root = (e.currentTarget as HTMLElement).closest("[data-lp-root]");
          const firstField = root ? root.querySelector<HTMLElement>("[data-lp-signup-first-field]") : null;
          if (firstField) firstField.focus();
          else onOpenAttend?.();
        } : undefined}
        style={{ marginTop: 20, background: mode === "draft" ? "rgba(255,255,255,0.6)" : T.white, color: theme.brand, fontWeight: 700, padding: "10px 18px", borderRadius: 6, border: "none", cursor: mode === "draft" ? "default" : "pointer", fontSize: 13 }}>{String(c.ctaLabel || "Register")}</button> : null}
      </div>
    </div>);
  }
  if (block.type === "dates") {
    // WYSIWYG: pre-formatted, tz-aware label + venue from the canonical event.
    // dateLabel arrives COMBINED ("Sat, Jul 25, 2026 · 11:00 AM — 11:45 AM")
    // from both the builder and the published surface — split it across the
    // Date and Time cards instead of cramming it all into Date and rendering
    // Time as "—" (operator report, 2026-07-13).
    const dlParts = event?.dateLabel ? String(event.dateLabel).split(" · ") : null;
    const dDate = dlParts ? dlParts[0] : c.date;
    const dTime = dlParts ? dlParts.slice(1).join(" · ") : c.time;
    const dLoc = event?.venueLabel ?? c.loc;
    // Multi-day: one combined DATE & TIME section listing every day, instead
    // of a single Date card showing only the first day and a Time card
    // showing the whole range as if it were one sitting (PM report, 2026-07-31).
    const multiDayList = Array.isArray(event?.days) && event.days.length > 1 ? event.days : null;
    if (c.style === "inline") {
      if (multiDayList) return wrap(<div className="lp-sec" style={{ padding: "16px 32px", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ ...lpInner, display: "flex", flexDirection: "column", gap: 8, fontSize: 13, color: T.n1 }}>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            {multiDayList.map((d, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ color: theme.brand }}><Icon.calendar size={14}/></span>{d.dateLabel} · {d.timeLabel}</span>
            ))}
          </div>
          {dLoc ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ color: theme.brand }}><Icon.loc size={14}/></span>{String(dLoc)}</span> : null}
        </div>
      </div>);
      return wrap(<div className="lp-sec" style={{ padding: "16px 32px", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ ...lpInner, display: "flex", gap: 24, fontSize: 13, color: T.n1, flexWrap: "wrap" }}>
          {[{ i: <Icon.calendar size={14}/>, v: dDate }, { i: <Icon.clock size={14}/>, v: dTime }, { i: <Icon.loc size={14}/>, v: dLoc }].map((d, i) => (
            d.v ? <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ color: theme.brand }}>{d.i}</span>{String(d.v || "")}</span> : null
          ))}
        </div>
      </div>);
    }
    if (multiDayList) return wrap(<div className="lp-sec" style={{ padding: "20px 32px" }}>
      <div style={lpInner}>
      <div className="lp-dates-grid" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
        <div style={{ padding: 12, border: `1px solid ${T.border}`, borderRadius: 6, display: "flex", gap: 10 }}>
          <span style={{ color: theme.brand, flexShrink: 0, marginTop: 2 }}><Icon.calendar size={16}/></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: T.soft, textTransform: "uppercase", fontWeight: 600, marginBottom: 6 }}>{t("landing.builderHelpers.text19", "Date & time")}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {multiDayList.map((d, i) => (
                <div key={i} style={{ fontSize: 13, color: T.n1, fontWeight: 600, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span>{d.dateLabel}</span><span style={{ color: T.soft, fontWeight: 400 }}>{d.timeLabel}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={{ padding: 12, border: `1px solid ${T.border}`, borderRadius: 6, display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ color: theme.brand }}><Icon.loc size={16}/></span>
          <div><div style={{ fontSize: 10, color: T.soft, textTransform: "uppercase", fontWeight: 600 }}>{t("landing.builderHelpers.text20", "Location")}</div><div style={{ fontSize: 13, color: T.n1, fontWeight: 600 }}>{String(dLoc || "—")}</div></div>
        </div>
      </div>
      </div>
    </div>);
    return wrap(<div className="lp-sec" style={{ padding: "20px 32px" }}>
      <div style={lpInner}>
      <div className="lp-dates-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        {[{ i: <Icon.calendar size={16}/>, l: "Date", v: dDate }, { i: <Icon.clock size={16}/>, l: "Time", v: dTime }, { i: <Icon.loc size={16}/>, l: "Location", v: dLoc }].map((d, i) => (
          <div key={i} style={{ padding: 12, border: `1px solid ${T.border}`, borderRadius: 6, display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ color: theme.brand }}>{d.i}</span>
            <div><div style={{ fontSize: 10, color: T.soft, textTransform: "uppercase", fontWeight: 600 }}>{d.l}</div><div style={{ fontSize: 13, color: T.n1, fontWeight: 600 }}>{String(d.v || "—")}</div></div>
          </div>
        ))}
      </div>
      </div>
    </div>);
  }
  if (block.type === "speakers") {
    const speakers = resolveSpeakerItems(c);
    const photoEl = (sp: Speaker, i: number, size: number) => sp.photo
      ? <div style={{ width: size, height: size, borderRadius: 999, background: `url(${sp.photo}) center/cover`, flexShrink: 0 }}/>
      : <Avatar initials={initialsFrom(sp.name)} size={size} color={COLORS[i % COLORS.length]}/>;
    const socialRow = (sp: Speaker, center: boolean) => {
      const active = SOCIAL_PLATFORMS.filter((p) => String(sp.socials?.[p.id] || "").trim());
      if (!active.length) return null;
      return <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap", justifyContent: center ? "center" : "flex-start" }}>
        {active.map((p) => <span key={p.id} title={p.label} style={{ width: 22, height: 22, borderRadius: 999, background: T.n6, color: theme.brand, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><SocialGlyph id={p.id} size={12}/></span>)}
      </div>;
    };
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}>
      <div style={{ fontSize: 18, fontWeight: 700, color: T.n1, marginBottom: 12 }}>{String(c.title || "Speakers")}</div>
      {c.layout === "list" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {speakers.map((sp, i) => <div key={sp.id || i} style={{ display: "flex", gap: 12, padding: 12, border: `1px solid ${T.border}`, borderRadius: 8, alignItems: "flex-start" }}>
            {photoEl(sp, i, 48)}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{sp.name || t("landing.builderHelpers.text21", "Speaker {arg1}", { arg1: i + 1 })}</div>
              {sp.title ? <div style={{ fontSize: 12, color: T.soft }}>{sp.title}</div> : null}
              {sp.bio ? <div style={{ fontSize: 12.5, color: T.n2, marginTop: 6, lineHeight: 1.5 }}>{sp.bio}</div> : null}
              {socialRow(sp, false)}
            </div>
          </div>)}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 14 }}>
          {speakers.map((sp, i) => <div key={sp.id || i} style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
            {photoEl(sp, i, 60)}
            <div style={{ fontSize: 13, color: T.n1, fontWeight: 600, marginTop: 8 }}>{sp.name || t("landing.builderHelpers.text21", "Speaker {arg1}", { arg1: i + 1 })}</div>
            {sp.title ? <div style={{ fontSize: 11, color: T.soft, marginTop: 1 }}>{sp.title}</div> : null}
            {sp.bio ? <div style={{ fontSize: 11.5, color: T.n2, marginTop: 6, lineHeight: 1.45 }}>{sp.bio}</div> : null}
            {socialRow(sp, true)}
          </div>)}
        </div>
      )}
      </div>
    </div>);
  }
  if (block.type === "tickets") {
    // WYSIWYG: real tiers from the canonical event object (event.ticketTiers) —
    // never a baked mock. Req 2 (Option-2 docket, 2026-07-14): with 0 or 1 tier
    // there is NO ticket-selection region at all on live/draft surfaces — the
    // selector exists only at ≥2 tiers (a single tier still rides the register
    // payload via the sign-up block's default selection). Req 3: at ≥2 tiers
    // every tier is a mutually-exclusive selectable BUTTON — washed-out fill +
    // border unselected, selected-border treatment, first available selected by
    // default, sold-out disabled. The SAME renderer paints the builder canvas
    // and the attend page, so the two behave identically by construction.
    const fmtPrice = (t: { price?: number | string; priceMinor?: number; currency?: string; soldOut?: boolean }): string => {
      if (t.soldOut) return "Sold out";
      if (typeof t.price === "string" && t.price) return t.price;
      if (typeof t.price === "number") return t.price === 0 ? "Free" : `$${t.price}`;
      if (typeof t.priceMinor === "number") return t.priceMinor === 0 ? "Free" : new Intl.NumberFormat(undefined, { style: "currency", currency: t.currency || "USD" }).format(t.priceMinor / 100);
      return "Free";
    };
    const tiers = event?.ticketTiers ?? [];
    // Req 2 + pure-WYSIWYG (operator, 2026-07-14): with 0 or 1 tier the ticket
    // region renders NOTHING — in EVERY mode, including the builder canvas. The
    // canvas IS the live page; no authoring placeholder may appear on it that a
    // guest wouldn't see. The block stays selectable via the page outline.
    if (tiers.length < 2) return null;
    const showSold = !!c.showSold;
    const grid = c.layout === "grid";
    const tierIdAt = (t: { id?: string | number }, i: number) => (t.id != null ? String(t.id) : `t${i}`);
    const activeId = selectedTierId ?? (tiers[0] ? tierIdAt(tiers[0], 0) : null);
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}>
      <div style={{ fontSize: 18, fontWeight: 700, color: T.n1, marginBottom: 12 }}>{String(c.title || "Tickets")}</div>
      {(
      <div role="radiogroup" aria-label={t("landing.builderHelpers.selectATicket", "Select a ticket")} className={grid ? "lp-tiers-grid" : undefined} style={{ display: grid ? "grid" : "flex", gridTemplateColumns: grid ? "repeat(auto-fit, minmax(220px, 1fr))" : undefined, flexDirection: grid ? undefined : "column", gap: 10 }}>
        {tiers.map((t, i) => {
          const id = tierIdAt(t, i);
          const on = activeId === id;
          const soldOut = !!t.soldOut;
          return (
            /* ui-raw: renders the CUSTOMER-FACING published page inside the builder preview, filled with the tenant colour (theme.brand) — Studio Button inks from our brand token and must not render here */
            <button key={id} type="button"
              role="radio" aria-checked={on} disabled={soldOut}
              onClick={soldOut ? undefined : (e) => { e.stopPropagation(); onSelectTier?.(id); }} /* ui-local — ticket choice; persisted via the sign-up block's awaited register op */
              style={{ display: "flex", gap: 12, padding: "14px 16px", border: `1px solid ${on && !soldOut ? theme.brand : T.n4}`, boxShadow: on && !soldOut ? `inset 0 0 0 1px ${theme.brand}` : "none", borderRadius: 8, alignItems: "center", textAlign: "left", width: "100%", boxSizing: "border-box", fontFamily: "inherit", background: soldOut ? T.n6 : on ? T.white : T.n6, cursor: soldOut ? "not-allowed" : "pointer", opacity: soldOut ? 0.55 : 1, transition: "border-color 120ms, background 120ms, box-shadow 120ms" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: on && !soldOut ? T.n1 : T.n2 }}>{t.name}</div>
                {t.desc ? <div style={{ fontSize: 12, color: T.soft, marginTop: 1 }}>{t.desc}</div> : null}
                {showSold && typeof t.sold === "number" ? <div style={{ fontSize: 11, color: T.soft, marginTop: 2 }}>{t.sold} {translate("landing.builderHelpers.sold", "sold")}</div> : null}
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: soldOut ? T.soft : on ? theme.brand : T.n2, flexShrink: 0 }}>{fmtPrice(t)}</div>
            </button>
          );
        })}
      </div>
      )}
      </div>
    </div>);
  }
  if (block.type === "agenda") {
    const items = resolveAgendaItems(c);
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}>
      <div style={{ fontSize: 18, fontWeight: 700, color: T.n1, marginBottom: 12 }}>{String(c.title || "Agenda")}</div>
      <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 0 }}>
        {items.map((it, i) => <li key={i} style={{ display: "flex", gap: 14, padding: "10px 0" }}>
          <div style={{ minWidth: 80, fontSize: 13, fontWeight: 600, color: theme.brand }}>{it.time}</div>
          <div style={{ fontSize: 14, color: T.n1, fontWeight: 500 }}>{it.title}</div>
        </li>)}
      </ol>
      </div>
    </div>);
  }
  if (block.type === "events") {
    const evs = Array.isArray(hubEvents) ? hubEvents : [];
    const cols = Number(c.cols) || 2;
    const cats = [...new Set(evs.map((e) => e.tag).filter(Boolean))] as string[];
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}>
        {c.title ? <div style={{ fontSize: 20, fontWeight: 800, color: T.n1, letterSpacing: -0.4, marginBottom: 14 }}>{String(c.title)}</div> : null}
        {c.showFilters !== false && cats.length ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
            {["All", ...cats].map((f, i) => <span key={f} style={{ padding: "4px 12px", borderRadius: 999, fontSize: 11, fontWeight: 600, background: i === 0 ? theme.brand : "transparent", color: i === 0 ? T.white : T.n2, border: `1px solid ${i === 0 ? theme.brand : T.borderStrong}` }}>{f}</span>)}
          </div>
        ) : null}
        {evs.length ? (
          <div className="lp-events-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 14 }}>
            {evs.map((ev) => (
              <div key={ev.id} style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: T.white }}>
                <div style={{ height: 96, background: `linear-gradient(135deg, ${ev.hue || theme.brand}, ${alpha(ev.hue || theme.brand, 53)})`, position: "relative" }}>
                  {ev.date ? <span style={{ position: "absolute", top: 8, left: 8, background: T.white, padding: "3px 8px", borderRadius: 5, fontSize: 10, fontWeight: 700, color: T.n1 }}>{ev.date}</span> : null}
                </div>
                <div style={{ padding: 14 }}>
                  {ev.tag ? <div style={{ fontSize: 10, color: theme.brand, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{ev.tag}</div> : null}
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.n1, marginTop: 4, lineHeight: 1.25 }}>{ev.title}</div>
                  {ev.location ? <div style={{ fontSize: 12.5, color: T.soft, marginTop: 5 }}>{ev.location}</div> : null}
                  <div style={{ marginTop: 12 }}><span style={{ display: "inline-block", background: theme.brand, color: T.white, fontSize: 12, fontWeight: 600, padding: "7px 14px", borderRadius: 6 }}>{t("landing.builderHelpers.viewEvent", "View event")}</span></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: 28, textAlign: "center", color: T.soft, fontSize: 13, border: `1.5px dashed ${T.n4}`, borderRadius: 10 }}>{t("landing.builderHelpers.eventsAddedToThisHubAppearHere", "Events added to this hub appear here.")}</div>
        )}
      </div>
    </div>);
  }
  if (block.type === "map") {
    // Real map when the landing surface carries coordinates (venueGeo from the
    // data layer); otherwise the honest placeholder grid — never a fake map.
    const geo = event?.venueGeo;
    const lat = typeof geo?.lat === "number" ? geo.lat : null;
    const lng = typeof geo?.lng === "number" ? geo.lng : null;
    const h = Number(c.height) || 240;
    const addressLine = geo?.formattedAddress ?? event?.venueAddress ?? event?.venueLabel ?? String(c.address || "");
    if (lat != null && lng != null) {
      const dLng = 0.005, dLat = 0.003;
      const bbox = `${(lng - dLng).toFixed(6)},${(lat - dLat).toFixed(6)},${(lng + dLng).toFixed(6)},${(lat + dLat).toFixed(6)}`;
      return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
        <div style={lpInner}>
        <div style={{ fontSize: 18, fontWeight: 700, color: T.n1, marginBottom: 12 }}>{String(c.title || "Find us")}</div>
        <iframe title={t("landing.builderHelpers.eventLocationMap", "Event location map")} loading="lazy"
          src={`https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat}%2C${lng}`}
          style={{ width: "100%", height: h, border: `1px solid ${T.border}`, borderRadius: 6, display: "block" }}/>
        <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: T.n2 }}>{addressLine}</span>
          <a href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=16/${lat}/${lng}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, fontWeight: 600, color: theme.brand, textDecoration: "none" }}>{t("landing.builderHelpers.openInMaps", "Open in Maps →")}</a>
        </div>
        </div>
      </div>);
    }
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}>
      <div style={{ fontSize: 18, fontWeight: 700, color: T.n1, marginBottom: 12 }}>{String(c.title || "Find us")}</div>
      <div style={{ height: h, borderRadius: 6, background: `repeating-linear-gradient(0deg,${T.n6} 0 1px,transparent 1px 28px),repeating-linear-gradient(90deg,${T.n6} 0 1px,transparent 1px 28px),${T.n5}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon.loc size={28} style={{ color: theme.brand }}/>
      </div>
      <div style={{ marginTop: 10, fontSize: 13, color: T.n2 }}>{addressLine}</div>
      </div>
    </div>);
  }
  if (block.type === "faq") {
    const qs = (event?.faqItems && event.faqItems.length) ? event.faqItems : resolveFaqItems(c);
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}>
      <div style={{ fontSize: 18, fontWeight: 700, color: T.n1, marginBottom: 12 }}>{String(c.title || "FAQ")}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {qs.map((it, i) => <div key={i} style={{ padding: 12, border: `1px solid ${T.border}`, borderRadius: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.n1, display: "flex", justifyContent: "space-between", gap: 12 }}><span>{it.q}</span><Icon.chev size={12} dir="down"/></div>
          <div style={{ fontSize: 12, color: T.soft, marginTop: 6, lineHeight: 1.5 }}>{it.a}</div>
        </div>)}
      </div>
      </div>
    </div>);
  }
  if (block.type === "signup") {
    const regTiers = event?.ticketTiers ?? [];
    const regTierIdAt = (t: { id?: string | number }, i: number) => (t.id != null ? String(t.id) : `t${i}`);
    const selectedTier = regTiers.length
      ? (regTiers.find((t, i) => regTierIdAt(t, i) === selectedTierId) ?? regTiers[0])
      : null;
    return wrap(<div className="lp-sec" style={{ padding: "32px 32px 40px", background: T.n6 }}>
      <div style={{ maxWidth: 720, margin: "0 auto", width: "100%", boxSizing: "border-box" }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: T.n1, marginBottom: 12 }}>{String(c.title || "Register")}</div>
        <SignupBlock c={c} theme={theme} mode={mode} eventId={eventId} event={event} selectedTier={selectedTier} multiTier={regTiers.length > 1}/>
      </div>
    </div>);
  }
  if (block.type === "text") {
    const al = c.align === "center" ? "center" : "left";
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}>
        {c.heading ? <h2 style={{ fontSize: 22, fontWeight: 700, color: T.n1, margin: "0 0 8px", textAlign: al }}>{String(c.heading)}</h2> : null}
        <div style={{ fontSize: 14.5, lineHeight: 1.65, color: T.n2, textAlign: al, whiteSpace: "pre-wrap" }}>{String(c.body || "")}</div>
      </div>
    </div>);
  }
  if (block.type === "image") {
    const mw = Number(c.maxW) || 0;
    const r = c.rounded !== false ? 10 : 0;
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}>
        {c.imageUrl
          ? <img src={String(c.imageUrl)} alt={String(c.caption || "")} style={{ width: "100%", maxWidth: mw ? mw : "100%", borderRadius: r, display: "block", margin: "0 auto" }}/>
          : <div style={{ height: 220, maxWidth: mw ? mw : "100%", margin: "0 auto", borderRadius: r, background: `repeating-linear-gradient(45deg,${T.n6} 0 10px,${T.n5} 10px 20px)`, display: "flex", alignItems: "center", justifyContent: "center", color: T.soft, fontFamily: T.fontMono, fontSize: 12 }}>{t("landing.builderHelpers.image6105d6cc", "image")}</div>}
        {c.caption ? <div style={{ fontSize: 12.5, color: T.soft, textAlign: "center", marginTop: 8 }}>{String(c.caption)}</div> : null}
      </div>
    </div>);
  }
  if (block.type === "gallery") {
    const imgs = Array.isArray(c.images) ? (c.images as string[]) : [];
    const cols = Number(c.cols) || 3;
    const motion = String(c.motion || "grid");
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}>
        {c.title ? <div style={{ fontSize: 18, fontWeight: 700, color: T.n1, marginBottom: 12 }}>{String(c.title)}</div> : null}
        {imgs.length ? (
          motion === "grid"
            ? <div className="lp-gallery" style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 8 }}>
                {imgs.map((src, i) => <div key={i} style={{ paddingTop: "66%", position: "relative", borderRadius: 8, background: `url(${src}) center/cover`, border: `1px solid ${T.border}` }}/>)}
              </div>
            : <HScroller motion={motion} mode={mode}>{imgs.map((src, i) => <div key={i} style={{ flex: "0 0 auto", width: 260, height: 170, borderRadius: 8, background: `url(${src}) center/cover`, border: `1px solid ${T.border}` }}/>)}</HScroller>
        ) : <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13, border: `1.5px dashed ${T.n4}`, borderRadius: 8 }}>{t("landing.builderHelpers.addImagesInTheInspector", "Add images in the inspector.")}</div>}
      </div>
    </div>);
  }
  if (block.type === "video") {
    const v = resolveVideo(c.url);
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}>
        <div style={{ position: "relative", paddingTop: "56.25%", borderRadius: 10, overflow: "hidden", background: T.n1 }}>
          {v.embed ? <iframe title={t("landing.builderHelpers.video", "Video")} src={v.embed} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}/>
           : v.file ? <video src={v.file} controls style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", background: "#000" }}/>
           : <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: T.n5, fontFamily: T.fontMono, fontSize: 12, padding: 16, textAlign: "center" }}>{t("landing.builderHelpers.pasteAYoutubeVimeoOrMp4Url", "paste a YouTube, Vimeo, or .mp4 URL")}</div>}
        </div>
        {c.caption ? <div style={{ fontSize: 12.5, color: T.soft, textAlign: "center", marginTop: 8 }}>{String(c.caption)}</div> : null}
      </div>
    </div>);
  }
  if (block.type === "quote") {
    return wrap(<div className="lp-sec" style={{ padding: "28px 32px" }}>
      <div style={{ ...lpInner, maxWidth: 720 }}>
        <div style={{ fontSize: 40, lineHeight: 1, color: theme.brand, fontWeight: 800 }}>{t("landing.builderHelpers.ldquo", "&ldquo;")}</div>
        <blockquote style={{ margin: "4px 0 14px", fontSize: 19, lineHeight: 1.5, color: T.n1, fontWeight: 500 }}>{String(c.quote || "")}</blockquote>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {c.photo ? <div style={{ width: 40, height: 40, borderRadius: 999, background: `url(${c.photo}) center/cover`, flexShrink: 0 }}/> : null}
          <div>
            {c.name ? <div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{String(c.name)}</div> : null}
            {c.role ? <div style={{ fontSize: 12.5, color: T.soft }}>{String(c.role)}</div> : null}
          </div>
        </div>
      </div>
    </div>);
  }
  if (block.type === "countdown") {
    return wrap(<div className="lp-sec" style={{ padding: "28px 32px" }}>
      <div style={lpInner}>
        <CountdownBlock title={c.title ? String(c.title) : undefined} target={c.target ? String(c.target) : undefined}
          zone={bareZone(c.targetZone) || bareZone(event?.timezone) || undefined} theme={theme}/>
      </div>
    </div>);
  }
  if (block.type === "cta") {
    const al = c.align === "left" ? "left" : "center";
    const bg = String(c.bg || "") || theme.brand;
    const href = String(c.href || "").trim();
    const btnStyle: React.CSSProperties = { display: "inline-block", marginTop: 18, background: T.white, color: bg, fontWeight: 700, padding: "11px 20px", borderRadius: 6, border: "none", cursor: mode === "draft" ? "default" : "pointer", fontSize: 13.5, textDecoration: "none" };
    return wrap(<div className="lp-sec" style={{ padding: "36px 32px" }}>
      <div style={lpInner}>
        <div style={{ background: bg, color: T.white, borderRadius: 14, padding: "28px 26px", textAlign: al }}>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: -0.4 }}>{String(c.headline || "")}</div>
          {c.sub ? <div style={{ fontSize: 14.5, opacity: 0.92, marginTop: 6, maxWidth: 520, marginLeft: al === "center" ? "auto" : 0, marginRight: al === "center" ? "auto" : 0 }}>{String(c.sub)}</div> : null}
          {/* ui-raw: renders the CUSTOMER-FACING published page inside the builder preview, filled with the tenant colour (theme.brand) — Studio Button inks from our brand token and must not render here */}
          {href
            ? <a href={mode === "draft" ? undefined : href} target="_blank" rel="noopener noreferrer" style={btnStyle}>{String(c.cta || "Learn more")}</a>
            : <button
                disabled={mode === "draft"}
                onClick={mode === "live" ? (e) => {
                  const root = (e.currentTarget as HTMLElement).closest("[data-lp-root]");
                  const firstField = root ? root.querySelector<HTMLElement>("[data-lp-signup-first-field]") : null;
                  if (firstField) firstField.focus(); else onOpenAttend?.();
                } : undefined}
                style={btnStyle}>{String(c.cta || "Register")}</button>}
        </div>
      </div>
    </div>);
  }
  if (block.type === "nav") {
    const items = (Array.isArray(c.items) ? c.items : []) as { uid?: string; label?: string }[];
    const al = c.align === "center" ? "center" : c.align === "right" ? "flex-end" : "flex-start";
    return wrap(<div className="lp-sec" style={{ padding: "10px 32px", borderBottom: `1px solid ${T.border}`, background: T.white, position: c.sticky !== false ? "sticky" : "static", top: 0, zIndex: 5 }}>
      <div style={{ ...lpInner, display: "flex", gap: 18, justifyContent: al, flexWrap: "wrap", alignItems: "center" }}>
        {items.length
          ? items.map((it, i) => <a key={i} href={it.uid ? `#lp-${it.uid}` : undefined} style={{ fontSize: 13.5, fontWeight: 600, color: T.n1, textDecoration: "none", cursor: "pointer" }}>{it.label || t("landing.landingBuilderHelpers.section", "Section")}</a>)
          : <span style={{ fontSize: 12.5, color: T.soft }}>{t("landing.builderHelpers.pickSectionsToLinkInTheInspector", "Pick sections to link in the inspector.")}</span>}
      </div>
    </div>);
  }
  if (block.type === "chat") {
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px", background: T.n6 }}>
      <div style={lpInner}><ChatBlock c={c} theme={theme} mode={mode}/></div>
    </div>);
  }
  if (block.type === "contact") {
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}><ContactBlock c={c} theme={theme} mode={mode}/></div>
    </div>);
  }
  if (block.type === "sponsors") {
    const items = (Array.isArray(c.items) ? c.items : []) as Sponsor[];
    const gray = c.grayscale !== false;
    const cols = Number(c.cols) || 4;
    const motion = String(c.motion || "grid");
    const socialRow = (sp: Sponsor) => {
      const active = SOCIAL_PLATFORMS.filter((p) => String(sp.socials?.[p.id] || "").trim());
      if (!active.length) return null;
      return <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap", marginTop: 2 }}>
        {active.map((p) => <span key={p.id} title={p.label} style={{ width: 22, height: 22, borderRadius: 999, background: T.n6, color: theme.brand, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><SocialGlyph id={p.id} size={12}/></span>)}
      </div>;
    };
    const card = (sp: Sponsor, i: number, fixed: boolean) => (
      <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, textAlign: "center", padding: 16, border: `1px solid ${T.border}`, borderRadius: 10, ...(fixed ? { flex: "0 0 auto", width: 200 } : {}) }}>
        {sp.logo ? <img src={sp.logo} alt={sp.name || t("landing.builderHelpers.sponsor", "Sponsor")} style={{ height: 44, maxWidth: 150, objectFit: "contain", filter: gray ? "grayscale(1)" : "none", opacity: gray ? 0.85 : 1 }}/> : null}
        {sp.name ? <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{sp.name}</div> : (!sp.logo ? <div style={{ fontSize: 15, fontWeight: 700, color: T.n3 }}>{t("landing.builderHelpers.text22", "Sponsor {arg1}", { arg1: i + 1 })}</div> : null)}
        {sp.bio ? <div style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>{sp.bio}</div> : null}
        {socialRow(sp)}
      </div>
    );
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}>
        {c.title ? <div style={{ fontSize: 14, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 14, textAlign: "center" }}>{String(c.title)}</div> : null}
        {!items.length ? <div style={{ padding: 20, textAlign: "center", color: T.soft, fontSize: 13, border: `1.5px dashed ${T.n4}`, borderRadius: 8 }}>{t("landing.builderHelpers.addSponsorsInTheInspector", "Add sponsors in the inspector.")}</div>
         : motion === "grid"
          ? <div className="lp-gallery" style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16 }}>{items.map((sp, i) => card(sp, i, false))}</div>
          : <HScroller motion={motion} mode={mode}>{items.map((sp, i) => card(sp, i, true))}</HScroller>}
      </div>
    </div>);
  }
  if (block.type === "stats") {
    const items = (Array.isArray(c.items) ? c.items : []) as { value?: string; label?: string }[];
    // §3b (2026-07-19): no fabricated stat rows — empty renders the same
    // inspector empty-state chrome the sponsors block uses.
    if (!items.length) return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}>
        <div style={{ padding: 20, textAlign: "center", color: T.soft, fontSize: 13, border: `1.5px dashed ${T.n4}`, borderRadius: 8 }}>{t("landing.builderHelpers.noStatsYetAddThemInTheInspector", "No stats yet — add them in the inspector.")}</div>
      </div>
    </div>);
    const list = items;
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={lpInner}>
        <div className="lp-stats-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(list.length, 4) || 1}, 1fr)`, gap: 14 }}>
          {list.map((s, i) => <div key={i} style={{ textAlign: "center", padding: "14px 8px", border: `1px solid ${T.border}`, borderRadius: 10 }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: theme.brand, lineHeight: 1 }}>{String(s.value || "")}</div>
            <div style={{ fontSize: 12.5, color: T.soft, marginTop: 6 }}>{String(s.label || "")}</div>
          </div>)}
        </div>
      </div>
    </div>);
  }
  if (block.type === "social") {
    const smode = String(c.smode || "follow");
    const links = (c.links as Record<string, string>) || {};
    const active = SOCIAL_PLATFORMS.filter((p) => String(links[p.id] || "").trim());
    const shareUrl = (id: string) => {
      const u = typeof window !== "undefined" ? window.location.href : "";
      const e = encodeURIComponent(u);
      return id === "x" ? `https://twitter.com/intent/tweet?url=${e}` : id === "facebook" ? `https://www.facebook.com/sharer/sharer.php?u=${e}` : `https://www.linkedin.com/sharing/share-offsite/?url=${e}`;
    };
    const chip = (id: string, href?: string) => (
      <a key={id} href={href} target="_blank" rel="noopener noreferrer"
        style={{ width: 40, height: 40, borderRadius: 999, background: T.n6, color: theme.brand, display: "inline-flex", alignItems: "center", justifyContent: "center", border: `1px solid ${T.border}`, textDecoration: "none" }}>
        <SocialGlyph id={id} size={17}/></a>
    );
    return wrap(<div className="lp-sec" style={{ padding: "24px 32px" }}>
      <div style={{ ...lpInner, textAlign: "center" }}>
        {c.title ? <div style={{ fontSize: 15, fontWeight: 700, color: T.n1, marginBottom: 12 }}>{String(c.title)}</div> : null}
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          {smode === "follow"
            ? (active.length ? active.map((p) => chip(p.id, links[p.id])) : <span style={{ fontSize: 13, color: T.soft }}>{t("landing.builderHelpers.addSocialLinksInTheInspector", "Add social links in the inspector.")}</span>)
            : ["linkedin", "x", "facebook"].map((id) => chip(id, mode === "live" ? shareUrl(id) : undefined))}
        </div>
      </div>
    </div>);
  }
  return wrap(<div style={{ padding: 24, color: T.soft, fontSize: 13 }}>{block.type}</div>);
}

// ── LandingPreview ──────────────────────────────────────────────────────
export function LandingPreview({ blocks, theme, font, slug, event, mode = "builder", eventId, onOpenAttend, hubEvents, chrome = true, hostLabel, selectedUid, onSelect, onRemove, onMove, dragOver, onDragOverBlock, onDrop, onDragStartBlock, onCellDrop, onDuplicate, renderCustom }: {
  blocks: LandingBlock[]; theme: LandingTheme; font?: string; slug?: string;
  event?: PublicEventDetailLike | null; mode?: LandingRenderMode; eventId?: string; onOpenAttend?: () => void; hubEvents?: HubEventCard[]; chrome?: boolean; hostLabel?: string;
  selectedUid?: string | null; onSelect?: (uid: string) => void; onRemove?: (uid: string) => void; onMove?: (uid: string, dir: number) => void;
  dragOver?: string | null; onDragOverBlock?: (uid: string) => void; onDrop?: (uid: string) => void; onDragStartBlock?: (uid: string) => void;
  onCellDrop?: (rowUid: string, cellIdx: number) => void; onDuplicate?: (uid: string) => void;
  renderCustom?: (block: LandingBlock) => React.ReactNode;
}) {
  const t = useT();
  const fontFamily = (font || theme.font) + ", DM Sans, system-ui, sans-serif";
  // Single-select ticket choice shared across the tickets + sign-up blocks.
  // Lives here so the tickets radio group and the sign-up submit agree, and so
  // the builder preview (event hub) and the published surface behave identically.
  const tierKey = (event?.ticketTiers ?? []).map((t, i) => `${t.soldOut ? 1 : 0}:${t.id != null ? String(t.id) : `t${i}`}`).join("|");
  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);
  useEffect(() => {
    const entries = tierKey ? tierKey.split("|") : [];
    const ids = entries.map((s) => s.slice(2));
    if (ids.length === 0) { setSelectedTierId((cur) => (cur === null ? cur : null)); return; }
    // Req 3: the first AVAILABLE tier is selected by default; a choice the
    // guest already made sticks while that tier still exists.
    const firstOpen = entries.find((s) => s.startsWith("0:"));
    const dflt = firstOpen ? firstOpen.slice(2) : ids[0];
    setSelectedTierId((cur) => (cur && ids.includes(cur) ? cur : dflt));
  }, [tierKey]);
  return (
    <div data-lp-root style={{ background: T.white, borderRadius: chrome ? 8 : 0, boxShadow: chrome ? "0 8px 32px rgba(0,0,0,0.12)" : "none", overflow: "hidden", border: chrome ? `1px solid ${T.border}` : "none", fontFamily, containerType: "inline-size" }}>
      <style dangerouslySetInnerHTML={{ __html: `
        @container (max-width: 560px) {
          .lp-hero, .lp-sec { padding-left: 20px !important; padding-right: 20px !important; }
          .lp-hero h1 { font-size: 26px !important; }
          .lp-dates-grid, .lp-tiers-grid, .lp-name-grid, .lp-pay-grid { grid-template-columns: 1fr !important; }
          .lp-row-stack { grid-template-columns: 1fr !important; }
          .lp-gallery, .lp-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .lp-events-grid { grid-template-columns: 1fr !important; }
        }
        @container (min-width: 900px) {
          .lp-hero, .lp-sec { padding-left: 48px !important; padding-right: 48px !important; }
          .lp-hero h1 { font-size: 44px !important; }
          .lp-hero .lp-sub { font-size: 16px !important; max-width: 560px !important; }
        }
      ` }}/>
      {chrome ? (
      <div style={{ padding: "10px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.soft, background: T.n6 }}>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: "#FF5F57" }}/>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: "#FEBC2E" }}/>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: "#28C840" }}/>
        <span style={{ marginLeft: 12, padding: "2px 10px", borderRadius: 4, background: T.white, border: `1px solid ${T.border}`, fontFamily: T.fontMono, fontSize: 11 }}>{hostLabel ? `${hostLabel}/${slug || t("landing.builder.slugPlaceholder", "your-event")}` : (slug || "your-event")}</span>
      </div>
      ) : null}
      {mode === "draft" ? (
        <div role="status" style={{ display: "flex", gap: 10, alignItems: "center", padding: "12px 16px", background: T.orange2, borderBottom: `1px solid ${alpha(T.orange1, "66")}`, color: T.n1, fontSize: 13, fontWeight: 600 }}>
          <Icon.warn size={15} style={{ color: T.orange1, flexShrink: 0 }}/>
          <span>{t("landing.builderHelpers.thisEventIsADraftRegistrationIsnTOpenYet", "This event is a draft — registration isn't open yet.")}</span>
          <span style={{ marginLeft: "auto", fontSize: 10.5, fontWeight: 700, color: T.orange1, textTransform: "uppercase", letterSpacing: 0.5, border: `1px solid ${T.orange1}`, borderRadius: 999, padding: "2px 8px", flexShrink: 0, whiteSpace: "nowrap" }}>{t("landing.builderHelpers.draftPreview", "Draft preview")}</span>
        </div>
      ) : null}
      {blocks.length === 0 ? (
        <div style={{ padding: 60, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("landing.builderHelpers.emptyPageDragABlockToStart", "Empty page — drag a block to start.")}</div>
      ) : blocks.map(b => (
        <RenderedBlock key={b.uid} block={b} theme={theme} event={event} mode={mode} eventId={eventId} onOpenAttend={onOpenAttend} hubEvents={hubEvents} selected={selectedUid === b.uid}
          selectedTierId={selectedTierId} onSelectTier={setSelectedTierId}
          selectedUid={selectedUid} dragOverKey={dragOver} onCellDrop={onCellDrop}
          onSelect={onSelect} onRemove={onRemove} onMove={onMove} onDuplicate={onDuplicate}
          dragOver={dragOver === b.uid} onDragOverBlock={onDragOverBlock}
          onDrop={onDrop} onDragStartBlock={onDragStartBlock} renderCustom={renderCustom}/>
      ))}
    </div>
  );
}
